import { useEffect, useMemo, useState, startTransition } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Drawer from "@mui/material/Drawer";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useSearchParams } from "react-router-dom";
import {
  biltFetch,
  clearBiltSessionToken,
  getBiltSessionToken,
  getDomainGateSession,
  isAllowedBiltPortalEmail,
  isComedyuoEmail,
  requestBiltAccess,
  roleForAllowedEmail,
  storeBiltSessionToken,
  storeDomainGateSession,
  verifyBiltMagic,
  type BiltRole,
} from "./api";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isUuid(value: string | null | undefined): boolean {
  return Boolean(value && UUID_RE.test(String(value)));
}

async function ensureBiltApiSession(): Promise<{ ok: boolean; error?: string }> {
  if (getBiltSessionToken()) return { ok: true };
  const gate = getDomainGateSession();
  if (!gate?.email) {
    return { ok: false, error: "Sign in with a @comedyuo.com or @bilt.com email first." };
  }
  const res = await requestBiltAccess(gate.email);
  if (res.ok && res.data.session_token) {
    storeBiltSessionToken(res.data.session_token);
    return { ok: true };
  }
  if (res.status === 404) {
    return {
      ok: false,
      error:
        "BILT portal routes are missing on cuo-cmd-service. Redeploy the API from current main (next semver).",
    };
  }
  return {
    ok: false,
    error: res.error || `Could not open a BILT API session (${res.status || "network"}).`,
  };
}

const BILT_LOGO = "/images/private-events-BILT_Rewards_Logo.png";
const displayFont = '"Syne", "Helvetica Neue", sans-serif';
const bodyFont = '"DM Sans", "Helvetica Neue", sans-serif';
const paper = "#07090d";
const mist = "rgba(255,255,255,0.12)";
const ink = "#f5f7fb";
const accent = "#c9a86a";
const muted = "rgba(245,247,251,0.58)";
const panelInk = "#f5f7fb";
const panelMuted = "rgba(245,247,251,0.58)";
const panelMist = "rgba(255,255,255,0.08)";
const ctaBlue = "#a8d4f0";
const glassCardSx = {
  bgcolor: "rgba(18, 22, 30, 0.55)",
  color: panelInk,
  border: "1px solid rgba(255,255,255,0.12)",
  backdropFilter: "blur(22px) saturate(1.2)",
  WebkitBackdropFilter: "blur(22px) saturate(1.2)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08), 0 18px 48px rgba(0,0,0,0.35)",
} as const;
const logoSx = {
  height: 28,
  width: "auto",
  display: "block",
  objectFit: "contain" as const,
  filter: "brightness(0) invert(1)",
};
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    color: ink,
    fontFamily: bodyFont,
    bgcolor: "rgba(255,255,255,0.06)",
    "& fieldset": { borderColor: mist },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.28)" },
    "&.Mui-focused fieldset": { borderColor: "rgba(168,212,240,0.65)" },
  },
  "& .MuiInputLabel-root": { color: "rgba(245,247,251,0.55)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "rgba(168,212,240,0.9)" },
  "& .MuiInputBase-input::placeholder": { color: "rgba(245,247,251,0.4)", opacity: 1 },
  "& option": { color: "#0a0e14", bgcolor: "#fff" },
};

const shellSx = {
  minHeight: "100vh",
  bgcolor: paper,
  color: ink,
  fontFamily: bodyFont,
  backgroundColor: paper,
  backgroundImage: [
    "radial-gradient(ellipse 70% 55% at 15% 0%, rgba(100,160,210,0.12), transparent 55%)",
    "radial-gradient(ellipse 55% 45% at 90% 8%, rgba(201,168,106,0.05), transparent 50%)",
    "radial-gradient(ellipse 90% 60% at 50% 110%, rgba(40,70,100,0.28), transparent 58%)",
    "linear-gradient(180deg, #0b1018 0%, #080b10 50%, #06080c 100%)",
  ].join(", "),
  "& .MuiTypography-root": {
    fontFamily: "inherit",
  },
  "@keyframes biltFadeUp": {
    from: { opacity: 0, transform: "translateY(10px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes biltSoftPulse": {
    "0%, 100%": { opacity: 0.55 },
    "50%": { opacity: 1 },
  },
} as const;

function ensureBiltFonts() {
  if (typeof document === "undefined") return;
  if (document.getElementById("bilt-portal-fonts")) return;
  const link = document.createElement("link");
  link.id = "bilt-portal-fonts";
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&family=Syne:wght@500;600;700;800&display=swap";
  document.head.appendChild(link);
}

type TabId = "upcoming" | "venues" | "bank" | "admin";

type ProgrammingKind = "comedy" | "magic";

type Venue = {
  id: string;
  name: string;
  market: string;
  programming?: ProgrammingKind | string;
  neighborhood: string;
  address?: string;
  capacity_text: string;
  pricing_text?: string;
  walkthrough_video_url?: string;
  why_excited: string;
  hosted_before: boolean | null;
  is_new_to_portal: boolean;
  photo_urls: string[];
  show_types: string[];
  meaningfully_updated_at: string;
  /** Latest reaction on this venue (any partner), for card tags. */
  reaction?: string | null;
  my_reaction?: string | null;
};

type EventRow = {
  id: string;
  title: string;
  event_date: string;
  calendar_year?: number;
  market: string;
  programming?: ProgrammingKind | string;
  show_type: string;
  status: string;
  source?: string;
  description: string;
  partner_notes: string;
  photo_urls: string[];
  venue_name?: string | null;
  venue_id?: string | null;
  venue_capacity_text?: string | null;
  venue_neighborhood?: string | null;
  venue_address?: string | null;
  next_steps?: string | null;
  doors_time?: string | null;
  show_start?: string | null;
  show_end?: string | null;
};

type OpenDate = {
  id: string;
  start_date: string;
  end_date?: string | null;
  market: string;
  show_type: string;
  notes: string;
  suggested_venue_names: string[];
};

type VenueComment = {
  id: string;
  author_email: string;
  body: string;
  created_at: string;
};

const LOCAL_COMMENTS_KEY = "cuo_bilt_portal_local_comments";
const LOCAL_REACTIONS_KEY = "cuo_bilt_portal_local_reactions";

function reactionLabel(value: string | null | undefined) {
  if (value === "interested") return "Interested";
  if (value === "not_a_fit") return "Not a fit";
  if (value === "need_more_info") return "Need more info";
  return "";
}

function readLocalReactions(): Record<string, string> {
  try {
    const raw = sessionStorage.getItem(LOCAL_REACTIONS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, string>;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function bankLocalReaction(venueId: string, value: string) {
  const all = readLocalReactions();
  if (!value) {
    delete all[venueId];
  } else {
    all[venueId] = value;
  }
  try {
    sessionStorage.setItem(LOCAL_REACTIONS_KEY, JSON.stringify(all));
  } catch {
    // ignore
  }
}

function withLocalReactions(rows: Venue[]): Venue[] {
  const local = readLocalReactions();
  return rows.map((raw) => {
    const v = enrichVenue(raw);
    const mined = local[v.id];
    if (!mined) return v;
    return { ...v, reaction: mined, my_reaction: mined };
  });
}

function readLocalComments(): Record<string, VenueComment[]> {
  try {
    const raw = sessionStorage.getItem(LOCAL_COMMENTS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, VenueComment[]>;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function localCommentsForVenue(venueId: string): VenueComment[] {
  return readLocalComments()[venueId] || [];
}

function bankLocalComment(venueId: string, comment: VenueComment): VenueComment[] {
  const all = readLocalComments();
  const next = [...(all[venueId] || []), comment];
  all[venueId] = next;
  try {
    sessionStorage.setItem(LOCAL_COMMENTS_KEY, JSON.stringify(all));
  } catch {
    // ignore quota / private mode
  }
  return next;
}

function formatDate(iso: string) {
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "America/New_York",
    });
  } catch {
    return iso;
  }
}

function monthKey(iso: string) {
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "America/New_York" });
}

function statusLabel(s: string) {
  if (s === "confirmed") return "Confirmed";
  if (s === "hold" || s === "proposal") return "Unconfirmed";
  if (s === "past") return "Past";
  return s;
}

function showTypeLabel(s: string) {
  if (s === "neighborhood_comedy") return "Neighborhood Comedy";
  if (s === "comedian_and_friends") return "Comedian and Friends";
  return "Other";
}

/** Extra locked-in nights past the general Nov confirm cutoff. */
const LOCKED_IN_EXTRA = new Set(["2026-12-09", "2026-12-14"]);

function isLockedInExtra(ev: EventRow) {
  if (!LOCKED_IN_EXTRA.has(ev.event_date)) return false;
  const blob = `${ev.title || ""} ${ev.venue_name || ""} ${ev.description || ""} ${ev.partner_notes || ""}`.toLowerCase();
  return (
    blob.includes("housing works") ||
    blob.includes("gianmarco") ||
    blob.includes("gianmcarco") ||
    blob.includes("georgia room")
  );
}

function isConfirmedShow(ev: EventRow) {
  if (isLockedInExtra(ev)) return true;
  const status = (ev.status || "").toLowerCase();
  if (status === "hold" || status === "proposal") return false;
  const notes = `${ev.partner_notes || ""} ${ev.description || ""}`.toLowerCase();
  if (/(^|[^a-z])(hold|tbd|tentative|unconfirmed|not confirmed)([^a-z]|$)/.test(notes)) {
    return false;
  }
  return status === "confirmed";
}

function isUpcomingEvent(ev: EventRow) {
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
  return Boolean(ev.event_date) && ev.event_date >= today;
}

function isPartnerEditableEvent(_ev: EventRow) {
  // Any upcoming card can be edited (timing, venue, details).
  return true;
}

function parseEventTimes(notes: string) {
  const text = notes || "";
  const doors = text.match(/Doors:\s*(.+)/i)?.[1]?.trim() || "";
  const start = text.match(/Show start:\s*(.+)/i)?.[1]?.trim() || "";
  const end = text.match(/Show end:\s*(.+)/i)?.[1]?.trim() || "";
  return { doors, start, end };
}

function parseNextSteps(notes: string) {
  const lines = (notes || "").split("\n");
  const out: string[] = [];
  let inNext = false;
  for (const line of lines) {
    if (/^\s*Next steps:\s*$/i.test(line)) {
      inNext = true;
      continue;
    }
    if (inNext && /^\s*(Doors|Show start|Show end):/i.test(line)) {
      inNext = false;
      continue;
    }
    if (inNext) out.push(line);
  }
  return out.join("\n").trim();
}

function eventNextSteps(ev: EventRow) {
  return (ev.next_steps || "").trim() || parseNextSteps(ev.partner_notes || "");
}

function isDroppedUpcoming(ev: EventRow) {
  if (ev.event_date !== "2026-11-19") return false;
  const blob = `${ev.title || ""} ${ev.venue_name || ""} ${ev.description || ""}`.toLowerCase();
  return blob.includes("magic") || blob.includes("american cut") || blob.includes("so & so");
}

const LOCAL_EVENTS_KEY = "cuo_bilt_portal_local_events";

function readLocalEvents(): EventRow[] {
  try {
    const raw = sessionStorage.getItem(LOCAL_EVENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as EventRow[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function bankLocalEvent(ev: EventRow): EventRow[] {
  const next = [...readLocalEvents().filter((e) => e.id !== ev.id), ev].sort((a, b) =>
    a.event_date.localeCompare(b.event_date)
  );
  try {
    sessionStorage.setItem(LOCAL_EVENTS_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
  return next;
}

function removeLocalEvent(id: string): EventRow[] {
  const next = readLocalEvents().filter((e) => e.id !== id);
  try {
    sessionStorage.setItem(LOCAL_EVENTS_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
  return next;
}

function mergeLocalEvents(rows: EventRow[]): EventRow[] {
  const byId = new Map<string, EventRow>();
  for (const ev of rows) byId.set(ev.id, ev);
  for (const ev of readLocalEvents()) byId.set(ev.id, ev);
  return Array.from(byId.values());
}

function sortUpcoming(rows: EventRow[]) {
  return rows
    .filter((ev) => isUpcomingEvent(ev) && !isDroppedUpcoming(ev))
    .sort((a, b) => a.event_date.localeCompare(b.event_date));
}

function isPastListedEvent(ev: EventRow) {
  return (ev.status || "").toLowerCase() === "past" || !isUpcomingEvent(ev);
}

/** Keep past bank shows when saving; only filter to upcoming when the show still is. */
function mergeEventIntoList(prev: EventRow[], next: EventRow): EventRow[] {
  const without = prev.filter((e) => e.id !== next.id);
  if (isPastListedEvent(next)) {
    return [...without, next].sort((a, b) => b.event_date.localeCompare(a.event_date));
  }
  return sortUpcoming([...without, next]);
}


const BILT_NEIGHBORHOOD_LOGO =
  "https://storage.googleapis.com/cuo-website/BILT%20Neighborhood%20Comedy.png";

function isFriendsShow(ev: EventRow) {
  if (ev.show_type === "comedian_and_friends") return true;
  const blob = `${ev.title || ""} ${ev.venue_name || ""} ${ev.description || ""} ${ev.partner_notes || ""}`.toLowerCase();
  return (
    blob.includes("comedian and friends") ||
    blob.includes("comedians and friends") ||
    blob.includes("bilt and friends") ||
    blob.includes("& friends") ||
    /\band friends\b/.test(blob)
  );
}

/** Title/venue keyword fills for shows that are not plain venue cards. */
const EVENT_TITLE_PHOTOS: { test: RegExp; url: string }[] = [
  {
    test: /\bvegas\b/i,
    url: "https://images.unsplash.com/photo-1605833556294-ea5c7a74f57d?w=1400&q=80",
  },
  {
    test: /gianmarco/i,
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/4e/GianmarcoSoresi-byPhilipRomano.jpg/1280px-GianmarcoSoresi-byPhilipRomano.jpg",
  },
  {
    test: /\busama\b/i,
    url: "https://storage.googleapis.com/cuo-website/Usama%20Desert%205.jpg",
  },
  {
    test: /crane\s*club/i,
    url: "https://images.unsplash.com/photo-1559339352-11d035aa65de?w=1400&q=80",
  },
  {
    test: /st\.?\s*theo/i,
    url: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=1400&q=80",
  },
  {
    test: /pj\s*clarke|p\.?j\.?\s*clarke/i,
    url: "https://images.unsplash.com/photo-1572116469696-31de0f17cc34?w=1400&q=80",
  },
  {
    test: /fette\s*sau/i,
    url: "https://images.unsplash.com/photo-1544025162-d76694265947?w=1400&q=80",
  },
  {
    test: /tin\s*building/i,
    url: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1400&q=80",
  },
  {
    test: /\bchicago\b/i,
    url: "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=1400&q=80",
  },
  {
    test: /\bboston\b|trident/i,
    url: "https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=1400&q=80",
  },
  {
    test: /phili|philly|middle\s*child/i,
    url: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=1400&q=80",
  },
  {
    test: /wilson/i,
    url: "https://storage.googleapis.com/cuo-website/Wilson+Show.jpg",
  },
  {
    test: /housing\s*works/i,
    url: "/images/partners/bilt/housing-works-bookstore.jpg",
  },
  {
    test: /antidote/i,
    url: "/images/partners/bilt/antidote-brooklyn.jpg",
  },
  {
    test: /ainslie/i,
    url: "/images/partners/bilt/ainslie-bk.jpg",
  },
  {
    test: /cooper\s*classic/i,
    url: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1400&q=80",
  },
  {
    test: /holiday\s*par/i,
    url: "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=1400&q=80",
  },
];

function eventTitlePhoto(ev: EventRow) {
  const blob = `${ev.title || ""} ${ev.venue_name || ""}`;
  for (const row of EVENT_TITLE_PHOTOS) {
    if (row.test.test(blob)) return row.url;
  }
  return "";
}

function eventImage(ev: EventRow, venues: Venue[] = []) {
  const fromTitle = eventTitlePhoto(ev);
  if (fromTitle) return fromTitle;
  const custom = ev.photo_urls?.[0]?.trim();
  if (custom && custom !== BILT_NEIGHBORHOOD_LOGO) return custom;
  const venue = resolveEventVenue(ev, venues);
  if (venue) {
    const fromVenue = resolveVenueCardPhoto(venue);
    if (fromVenue) return fromVenue;
  }
  const byName =
    curatedCardPhoto(ev.venue_name || "") || curatedCardPhoto(ev.title || "");
  if (byName) return byName;
  return BILT_NEIGHBORHOOD_LOGO;
}

function isEventBrandMark(url: string) {
  return !url || url === BILT_NEIGHBORHOOD_LOGO;
}

function resolveEventVenue(ev: EventRow, venues: Venue[]) {
  const byId = venues.find((v) => v.id && v.id === ev.venue_id);
  if (byId) return byId;
  const eventKey = venueKey(`${ev.venue_name || ""} ${ev.title || ""}`);
  if (!eventKey) return undefined;
  const exact = venues.find((v) => venueKey(v.name) === eventKey);
  if (exact) return exact;
  const compact = eventKey.replace(/\s/g, "");
  const fuzzy = venues
    .map((v) => ({ v, k: venueKey(v.name) }))
    .filter(({ k }) => {
      if (!k) return false;
      return (
        eventKey.includes(k) ||
        k.includes(eventKey) ||
        compact.includes(k.replace(/\s/g, "")) ||
        k.replace(/\s/g, "").includes(compact)
      );
    })
    .sort((a, b) => b.k.length - a.k.length);
  return fuzzy[0]?.v;
}

/** Location display: street address only, or TBD. Never NYC / Ecosystem / neighborhood. */
function isRealStreetAddress(raw: string) {
  const text = (raw || "").trim();
  if (!text) return false;
  const lower = text.toLowerCase();
  if (lower === "nyc" || lower === "new york" || lower === "new york city") return false;
  if (lower === "ecosystem" || lower.includes("ecosystem")) return false;
  if (lower === "tbd" || lower === "location tbd") return false;
  // Prefer lines that look like a street address (number or St/Ave/etc.).
  if (/\d/.test(text)) return true;
  return /\b(st|street|ave|avenue|blvd|road|rd|pl|place|hwy|highway|dr|drive)\b/i.test(text);
}

function displayAddressOrTbd(raw: string | null | undefined) {
  const text = (raw || "").trim();
  return isRealStreetAddress(text) ? text : "TBD";
}

function shortLocation(ev: EventRow, venues: Venue[]) {
  const stamped = (ev.venue_address || "").trim();
  if (isRealStreetAddress(stamped)) return stamped;
  const fromDir =
    addressForVenueName(ev.venue_name || "") ||
    addressForVenueName(ev.title || "") ||
    addressForVenueName(`${ev.title || ""} ${ev.venue_name || ""}`);
  if (fromDir) return fromDir;
  const venue = resolveEventVenue(ev, venues);
  return displayAddressOrTbd(venue?.address || "");
}

function venuePlaceLine(venue: Venue) {
  return displayAddressOrTbd(venue.address);
}

function filterPortalMarkets(list: string[]) {
  return list.filter((m) => (m || "").trim().toLowerCase() !== "ecosystem");
}

/** Drop calendar tags that are not useful partner copy. */
function cleanPartnerCopy(text: string | null | undefined) {
  const raw = (text || "").trim();
  if (!raw) return "";
  if (/^(private|public)(\s+event)?$/i.test(raw)) return "";
  return raw
    .split("\n")
    .filter((line) => !/^(private|public)(\s+event)?$/i.test(line.trim()))
    .join("\n")
    .trim();
}

function venueKey(name: string) {
  return (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^the /, "");
}

/** Curated ComedyUO / night fills for outward-facing venue cards. */
const VENUE_CARD_PHOTOS: Record<string, string> = {
  "zabar s": "https://storage.googleapis.com/cuo-website/Judy%20Gold%20Zabar%27s.jpg",
  zabars: "https://storage.googleapis.com/cuo-website/Judy%20Gold%20Zabar%27s.jpg",
  "apollo bagels": "https://storage.googleapis.com/cuo-website/Apollo%20Bagels.jpg",
  "desert 5": "https://storage.googleapis.com/cuo-website/Usama%20Desert%205.jpg",
  "padel haus": "https://storage.googleapis.com/cuo-website/Padel%20House.jpg",
  "padel house": "https://storage.googleapis.com/cuo-website/Padel%20House.jpg",
  "russ daughters": "https://storage.googleapis.com/cuo-website/Ian%20Lara%20Russ%20%26%20Daughters.jpg",
  "great jones": "https://storage.googleapis.com/cuo-website/Great%20Jones%20BILT.jpg",
  "great jones distillery": "https://storage.googleapis.com/cuo-website/Great%20Jones%20BILT.jpg",
  "drai s": "https://storage.googleapis.com/cuo-website/Drais.jpg",
  drais: "https://storage.googleapis.com/cuo-website/Drais.jpg",
  "drai s supper club": "https://storage.googleapis.com/cuo-website/Drais.jpg",
  "brass rail": "https://storage.googleapis.com/cuo-website/The%20Brass%20Rail.jpg",
  "south house": "https://storage.googleapis.com/cuo-website/South%20House%20NJ%20.jpg",
  lokal: "https://storage.googleapis.com/cuo-website/Lokal.jpg",
  porta: "https://storage.googleapis.com/cuo-website/Porta%20NJ.jpg",
  junto: "https://storage.googleapis.com/cuo-website/Junto%20Attic%20Bar.jpg",
  "the junto": "https://storage.googleapis.com/cuo-website/Junto%20Attic%20Bar.jpg",
  "barry s bootcamp wainscott": "/images/partners/bilt/barrys-bootcamp-wainscott.jpg",
  "barrys bootcamp wainscott": "/images/partners/bilt/barrys-bootcamp-wainscott.jpg",
  "barry s bootcamp": "/images/partners/bilt/barrys-bootcamp-wainscott.jpg",
  cipriani: "https://storage.googleapis.com/cuo-website/Cipriani%20Vault%20Room.jpg",
  "cipriani wall st": "https://storage.googleapis.com/cuo-website/Cipriani%20Vault%20Room.jpg",
  fini: "https://storage.googleapis.com/cuo-website/Fini.jpeg",
  serafina: "https://storage.googleapis.com/cuo-website/Serafina.webp",
  "vic s": "https://storage.googleapis.com/cuo-website/Vics.webp",
  vics: "https://storage.googleapis.com/cuo-website/Vics.webp",
  veselka: "https://storage.googleapis.com/cuo-website/Dan+Soder+at+Veselka.jpg",
  "astor place": "https://storage.googleapis.com/cuo-website/Astor%20Place%20Magic%20Show.jpg",
  lucali: "https://images.unsplash.com/photo-1513104890138-7c749659a591?w=1400&q=80",
  antidote: "/images/partners/bilt/antidote-brooklyn.jpg",
  "furnish green": "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=1400&q=80",
  "housing works": "/images/partners/bilt/housing-works-bookstore.jpg",
  "cooper classic cars": "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1400&q=80",
  "the seville": "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1400&q=80",
  seville: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1400&q=80",
  "gem home": "https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=1400&q=80",
  "abc cocina": "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1400&q=80",
  "pj clarke s sidear": "https://images.unsplash.com/photo-1572116469696-31de0f17cc34?w=1400&q=80",
  "pj clarke s sidecar": "https://images.unsplash.com/photo-1572116469696-31de0f17cc34?w=1400&q=80",
  "pj clarke s": "https://images.unsplash.com/photo-1572116469696-31de0f17cc34?w=1400&q=80",
  "pj clarkes": "https://images.unsplash.com/photo-1572116469696-31de0f17cc34?w=1400&q=80",
  "st theo s": "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=1400&q=80",
  "st theo": "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=1400&q=80",
  "crane club": "https://images.unsplash.com/photo-1559339352-11d035aa65de?w=1400&q=80",
  "fette sau": "https://images.unsplash.com/photo-1544025162-d76694265947?w=1400&q=80",
  "wilson store": "https://storage.googleapis.com/cuo-website/Wilson+Show.jpg",
  wilson: "https://storage.googleapis.com/cuo-website/Wilson+Show.jpg",
  "georgia room": "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1400&q=80",
  skewr: "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=1400&q=80",
  "record room": "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=1400&q=80",
  "roberta s bushwick": "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=1400&q=80",
  "paragon sports": "https://images.unsplash.com/photo-1556906781-9a412961c28c?w=1400&q=80",
  "ainslie bk": "/images/partners/bilt/ainslie-bk.jpg",
  ainslie: "/images/partners/bilt/ainslie-bk.jpg",
};

function curatedCardPhoto(name: string) {
  const key = venueKey(name);
  if (!key) return "";
  if (VENUE_CARD_PHOTOS[key]) return VENUE_CARD_PHOTOS[key];
  const hits = Object.entries(VENUE_CARD_PHOTOS).filter(([k]) => key.includes(k) || k.includes(key));
  return hits.length === 1 ? hits[0][1] : "";
}

/** Outward-facing card always prefers ComedyUO / curated photo over partner uploads. */
function resolveVenueCardPhoto(venue: Venue) {
  return curatedCardPhoto(venue.name) || (venue.photo_urls?.[0] || "").trim() || "";
}

function isVideoUrl(url: string) {
  return /\.(mp4|webm|mov)(\?|$)/i.test(url) || url.includes("video/");
}

function isImageUrl(url: string) {
  return /\.(jpe?g|png|gif|webp)(\?|$)/i.test(url) || url.startsWith("blob:") || url.startsWith("data:image");
}

/** Google Drive / Docs share links (not direct .mp4 URLs). */
function isGoogleDriveWalkthroughUrl(url: string) {
  const u = (url || "").trim().toLowerCase();
  return u.includes("drive.google.com") || u.includes("docs.google.com");
}

function googleDrivePreviewUrl(url: string) {
  const match = /(?:drive|docs)\.google\.com\/(?:file\/d\/|open\?id=)([a-zA-Z0-9_-]+)/i.exec(
    url || ""
  );
  const id =
    match?.[1] ||
    (/[?&]id=([a-zA-Z0-9_-]+)/i.exec(url || "")?.[1] ?? "");
  return id ? `https://drive.google.com/file/d/${id}/preview` : "";
}

/** Normalize pasted walkthrough text (Drive share links, stray spaces). */
function normalizeWalkthroughUrl(raw: string) {
  const text = (raw || "").trim();
  if (!text) return "";
  const found = text.match(/https?:\/\/[^\s<>"']+/i);
  const url = (found ? found[0] : text).replace(/[.,);'"\]]+$/g, "");
  const fileMatch = /(?:drive|docs)\.google\.com\/(?:file\/d\/|open\?id=)([a-zA-Z0-9_-]+)/i.exec(
    url
  );
  const id =
    fileMatch?.[1] ||
    ((/drive\.google\.com|docs\.google\.com/i.test(url) &&
      /[?&]id=([a-zA-Z0-9_-]+)/i.exec(url)?.[1]) ||
      "");
  if (id) return `https://drive.google.com/file/d/${id}/view`;
  return url;
}

/** Street addresses when the sheet only has neighborhood. */
const VENUE_ADDRESS_DIR: Record<string, string> = {
  lucali: "575 Henry St, Brooklyn, NY 11231",
  "zabar s": "2245 Broadway, New York, NY 10024",
  zabars: "2245 Broadway, New York, NY 10024",
  antidote: "66 S 2nd St, Brooklyn, NY 11249",
  "furnish green": "132 1/2 W 24th St, New York, NY 10011",
  "astor place": "21 Astor Pl, New York, NY 10003",
  "desert 5": "94 Wythe Ave, Brooklyn, NY 11249",
  "desert 5 spot": "94 Wythe Ave, Brooklyn, NY 11249",
  "stef gabbydesert 5": "94 Wythe Ave, Brooklyn, NY 11249",
  "gabbydesert 5": "94 Wythe Ave, Brooklyn, NY 11249",
  "russ daughters": "502 W 34th St, New York, NY 10001",
  "great jones": "25 Great Jones St, New York, NY 10012",
  "great jones distillery": "25 Great Jones St, New York, NY 10012",
  "padel haus": "307 Kent Ave, Brooklyn, NY 11249",
  "padel house": "307 Kent Ave, Brooklyn, NY 11249",
  "drai s": "377 Park Ave S, New York, NY 10016",
  drais: "377 Park Ave S, New York, NY 10016",
  "drai s supper club": "377 Park Ave S, New York, NY 10016",
  "wilson store": "594 Broadway, New York, NY 10012",
  "apollo bagels": "242 E 10th St, New York, NY 10003",
  "housing works": "126 Crosby St, New York, NY 10012",
  "cooper classic cars": "37 W 20th St, New York, NY 10011",
  "the seville": "22 E 29th St, New York, NY 10016",
  seville: "22 E 29th St, New York, NY 10016",
  "barry s bootcamp wainscott": "352 Montauk Highway, Wainscott, NY 11975",
  "barrys bootcamp wainscott": "352 Montauk Highway, Wainscott, NY 11975",
  "barry s bootcamp": "352 Montauk Highway, Wainscott, NY 11975",
  "gem home": "181 Mott St, New York, NY 10012",
  "abc cocina": "38 E 19th St, New York, NY 10003",
  fini: "90 Prince St, New York, NY 10012",
  "pj clarke s sidear": "212 E 55th St, New York, NY 10022",
  "pj clarke s sidecar": "212 E 55th St, New York, NY 10022",
  "georgia room": "25 W 56th St, New York, NY 10019",
  skewr: "55 Bond St, New York, NY 10012",
  "record room": "90 Morton St, New York, NY 10014",
  "roberta s bushwick": "261 Moore St, Brooklyn, NY 11206",
  "paragon sports": "867 Broadway, New York, NY 10003",
  "ainslie bk": "113 Ainslie St, Brooklyn, NY 11211",
  ainslie: "113 Ainslie St, Brooklyn, NY 11211",
  cipriani: "55 Wall St, New York, NY 10005",
  "cipriani wall st": "55 Wall St, New York, NY 10005",
  porta: "131 Newark Ave, Jersey City, NJ 07302",
  lokal: "1314 Washington St, Hoboken, NJ 07030",
  "brass rail": "135 Washington St, Hoboken, NJ 07030",
  "south house": "1500 Willow Ave, Hoboken, NJ 07030",
  junto: "45 Main St, Brooklyn, NY 11201",
  "the junto": "45 Main St, Brooklyn, NY 11201",
  serafina: "2178 Broadway, New York, NY 10024",
  "vic s": "31 Great Jones St, New York, NY 10012",
  vics: "31 Great Jones St, New York, NY 10012",
  veselka: "144 2nd Ave, New York, NY 10003",
};

function addressForVenueName(name: string) {
  const key = venueKey(name);
  if (!key) return "";
  if (VENUE_ADDRESS_DIR[key]) return VENUE_ADDRESS_DIR[key];
  const compact = key.replace(/\s/g, "");
  const hits = Object.entries(VENUE_ADDRESS_DIR)
    .filter(([k]) => {
      const ck = k.replace(/\s/g, "");
      return key.includes(k) || k.includes(key) || compact.includes(ck) || ck.includes(compact);
    })
    .sort((a, b) => b[0].length - a[0].length);
  return hits[0]?.[1] || "";
}

/** Local fills until sheet sync / upload lands the same values in the API. */
const VENUE_WALKTHROUGH_DIR: Record<string, string> = {
  "ainslie bk": "/images/partners/bilt/ainslie-bk-walkthrough.mp4",
  ainslie: "/images/partners/bilt/ainslie-bk-walkthrough.mp4",
};

const VENUE_PRICING_DIR: Record<string, string> = {
  "ainslie bk": "$7,727",
  ainslie: "$7,727",
};

function lookupVenueDir(dir: Record<string, string>, name: string) {
  const key = venueKey(name);
  if (!key) return "";
  if (dir[key]) return dir[key];
  const hits = Object.entries(dir)
    .filter(([k]) => key.includes(k) || k.includes(key))
    .sort((a, b) => b[0].length - a[0].length);
  return hits[0]?.[1] || "";
}

/** Walkthrough lives on the venue; upcoming shows reuse it so moves keep the video. */
function eventWalkthrough(ev: EventRow, venues: Venue[]) {
  const venue = resolveEventVenue(ev, venues);
  const fromVenue = (venue?.walkthrough_video_url || "").trim();
  if (fromVenue) return fromVenue;
  return (
    lookupVenueDir(VENUE_WALKTHROUGH_DIR, venue?.name || "") ||
    lookupVenueDir(VENUE_WALKTHROUGH_DIR, ev.venue_name || "") ||
    lookupVenueDir(VENUE_WALKTHROUGH_DIR, ev.title || "")
  );
}

function enrichVenue(v: Venue): Venue {
  const raw = (v.address || "").trim();
  // Sheet often stores neighborhood here; prefer a real public street address.
  const address = isRealStreetAddress(raw)
    ? raw
    : addressForVenueName(v.name) || raw;
  const curated = curatedCardPhoto(v.name);
  const photos = [...(v.photo_urls || []).filter(Boolean)];
  if (curated && !photos.includes(curated)) {
    // Keep curated first for cards; partner uploads stay after it.
    photos.unshift(curated);
  }
  const walkthrough =
    (v.walkthrough_video_url || "").trim() ||
    lookupVenueDir(VENUE_WALKTHROUGH_DIR, v.name);
  const pricing =
    (v.pricing_text || "").trim() || lookupVenueDir(VENUE_PRICING_DIR, v.name);
  return {
    ...v,
    address,
    photo_urls: photos,
    pricing_text: pricing,
    walkthrough_video_url: walkthrough,
    why_excited: cleanPartnerCopy(v.why_excited),
  };
}

/** Direct multipart through Cloud Run must stay under the ~32 MB body cap. */
const BILT_MULTIPART_MAX_BYTES = 30 * 1024 * 1024;
/** Direct-to-GCS signed PUT for longer walkthroughs. */
const BILT_SIGNED_MAX_BYTES = 200 * 1024 * 1024;

async function uploadBiltFileViaSignedUrl(
  file: File
): Promise<{ url?: string; error?: string }> {
  const sign = await biltFetch<{
    upload_url: string;
    public_url: string;
    content_type: string;
  }>("upload/sign/", {
    method: "POST",
    body: JSON.stringify({
      filename: file.name || "walkthrough.mp4",
      content_type: file.type || "video/mp4",
      size: file.size,
    }),
  });
  if (!sign.ok || !sign.data?.upload_url || !sign.data?.public_url) {
    return {
      error:
        sign.error ||
        "Large upload is unavailable. Paste a Google Drive share link instead.",
    };
  }
  try {
    const put = await fetch(sign.data.upload_url, {
      method: "PUT",
      headers: { "Content-Type": sign.data.content_type || file.type || "video/mp4" },
      body: file,
    });
    if (!put.ok) {
      return {
        error:
          "Could not upload that large file to storage. Paste a Google Drive share link instead.",
      };
    }
  } catch {
    return {
      error:
        "Could not upload that large file to storage. Paste a Google Drive share link instead.",
    };
  }
  return { url: sign.data.public_url };
}

async function uploadBiltFile(file: File): Promise<{ url?: string; error?: string }> {
  if (file.size > BILT_SIGNED_MAX_BYTES) {
    return {
      error:
        "File is too large (max 200 MB). Compress the clip or paste a Google Drive share link.",
    };
  }
  if (file.size > BILT_MULTIPART_MAX_BYTES) {
    return uploadBiltFileViaSignedUrl(file);
  }
  // Multipart stays under Cloud Run's 32 MB body limit; base64 JSON does not.
  const body = new FormData();
  body.append("file", file, file.name || "upload");
  const res = await biltFetch<{ url: string }>("upload/", {
    method: "POST",
    body,
  });
  if (res.ok && res.data?.url) return { url: res.data.url };
  if (res.status === 404) {
    return {
      error:
        "Upload API is not live yet. Redeploy cuo-cmd-service from current main, then try again.",
    };
  }
  if (res.status === 413) {
    return uploadBiltFileViaSignedUrl(file);
  }
  return { error: res.error || "Upload failed." };
}

function resolveEventCapacity(ev: EventRow, venues: Venue[] = []) {
  const fromEvent = (ev.venue_capacity_text || "").trim();
  if (fromEvent) return fromEvent;
  const venue = venues.find(
    (v) =>
      v.id === ev.venue_id ||
      v.name.toLowerCase() === (ev.venue_name || "").toLowerCase() ||
      v.name.toLowerCase() === (ev.title || "").toLowerCase()
  );
  return (venue?.capacity_text || "").trim();
}

function capacityLabel(raw: string | null | undefined) {
  const text = (raw || "").trim();
  return text ? `Capacity ${text}` : "Capacity not set";
}

function marketLabel(market: string) {
  const raw = (market || "").trim();
  if (!raw) return "";
  if (/ecosystem/i.test(raw)) return "";
  if (/hoboken|jersey/i.test(raw)) return "NJ";
  return raw;
}

function isNjMarket(market: string) {
  return /hoboken|jersey/i.test(market || "");
}

function normalizeProgramming(raw: string | null | undefined): ProgrammingKind {
  const v = (raw || "").trim().toLowerCase();
  if (v === "magic") return "magic";
  return "comedy";
}

function programmingLabel(raw: string | null | undefined) {
  return normalizeProgramming(raw) === "magic" ? "Magic" : "Comedy";
}

function eventProgramming(ev: EventRow, venues: Venue[] = []): ProgrammingKind {
  if (ev.programming) return normalizeProgramming(ev.programming);
  const venue = resolveEventVenue(ev, venues);
  return normalizeProgramming(venue?.programming);
}

function eventMatchesMarket(ev: EventRow, market: string) {
  if (!market) return true;
  if (ev.market === market) return true;
  if (!isNjMarket(market)) return false;
  const blob = `${ev.market || ""} ${ev.title || ""} ${ev.venue_name || ""}`;
  return /\b(jersey|hoboken|porta|lokal|brass\s+rail|south\s+house)\b/i.test(blob);
}

function venueMatchesMarket(v: Venue, market: string) {
  if (!market) return true;
  if (v.market === market) return true;
  if (v.neighborhood?.includes(market)) return true;
  if (!isNjMarket(market)) return false;
  return isNjMarket(v.market || "") || /\b(jersey|hoboken)\b/i.test(v.neighborhood || "");
}

function ProgrammingBadge({
  value,
  corner = "top-right",
}: {
  value: string | null | undefined;
  corner?: "top-right" | "top-left";
}) {
  const kind = normalizeProgramming(value);
  return (
    <Typography
      sx={{
        position: "absolute",
        top: 12,
        ...(corner === "top-left" ? { left: 12 } : { right: 12 }),
        zIndex: 2,
        fontFamily: bodyFont,
        fontSize: "0.65rem",
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        fontWeight: 700,
        color: kind === "magic" ? "#f5e6ff" : "#0a0e14",
        bgcolor: kind === "magic" ? "rgba(88, 48, 120, 0.92)" : accent,
        border: "1px solid rgba(255,255,255,0.18)",
        borderRadius: "999px",
        px: 1.1,
        py: 0.45,
        boxShadow: "0 8px 18px rgba(0,0,0,0.35)",
      }}
    >
      {kind === "magic" ? "Magic" : "Comedy"}
    </Typography>
  );
}

function collectMarkets(venues: Venue[], events: EventRow[] = []) {
  const set = new Set<string>();
  for (const v of venues) if (v.market?.trim()) set.add(v.market.trim());
  for (const e of events) if (e.market?.trim()) set.add(e.market.trim());
  return Array.from(set).sort((a, b) => marketLabel(a).localeCompare(marketLabel(b)));
}

function calendarDay(iso: string) {
  try {
    const d = new Date(`${iso}T12:00:00`);
    return {
      weekday: d.toLocaleDateString("en-US", { weekday: "short", timeZone: "America/New_York" }),
      month: d.toLocaleDateString("en-US", { month: "short", timeZone: "America/New_York" }),
      day: d.toLocaleDateString("en-US", { day: "numeric", timeZone: "America/New_York" }),
      year: d.toLocaleDateString("en-US", { year: "numeric", timeZone: "America/New_York" }),
    };
  } catch {
    return { weekday: "", month: "", day: iso, year: "" };
  }
}

function eventYear(ev: EventRow) {
  if (ev.calendar_year) return String(ev.calendar_year);
  return (ev.event_date || "").slice(0, 4);
}

function groupEventsByYear(rows: EventRow[]) {
  const groups = new Map<string, EventRow[]>();
  for (const ev of rows) {
    const year = eventYear(ev) || "Unknown";
    const list = groups.get(year) || [];
    list.push(ev);
    groups.set(year, list);
  }
  return Array.from(groups.entries()).sort((a, b) => b[0].localeCompare(a[0]));
}

export default function BiltPortalPage() {
  const [params, setParams] = useSearchParams();
  const [booting, setBooting] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<BiltRole | null>(null);
  const [meEmail, setMeEmail] = useState("");
  const [gateError, setGateError] = useState("");
  const [gateMessage, setGateMessage] = useState("");
  const [debugUrl, setDebugUrl] = useState("");
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState<TabId>("upcoming");
  const [apiDown, setApiDown] = useState(false);
  const [stale, setStale] = useState(false);
  const [lastSync, setLastSync] = useState<string | null>(null);
  const [overview, setOverview] = useState<{
    upcoming: EventRow[];
    new_venues: Venue[];
    open_dates: OpenDate[];
    updated_since_last_visit: string[];
  } | null>(null);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  // Full portal list for Add/Edit event picker (not the filtered Venues/Bank tab).
  const [allVenues, setAllVenues] = useState<Venue[]>([]);
  const [markets, setMarkets] = useState<string[]>([]);
  const [market, setMarket] = useState("");
  const [programming, setProgramming] = useState<"" | ProgrammingKind>("");
  const [query, setQuery] = useState("");
  const [newEventProgramming, setNewEventProgramming] = useState<ProgrammingKind>("comedy");
  const [venueProgramming, setVenueProgramming] = useState<ProgrammingKind>("comedy");
  const [monthCursor, setMonthCursor] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
  });
  const [selectedVenue, setSelectedVenue] = useState<string | null>(null);
  const [venueDetail, setVenueDetail] = useState<Record<string, unknown> | null>(null);
  const [commentBody, setCommentBody] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<BiltRole>("partner");
  const [adminMsg, setAdminMsg] = useState("");
  const [loadingTab, setLoadingTab] = useState(false);
  const [sheetPreviewLabel, setSheetPreviewLabel] = useState<string | null>(null);
  const [addEventOpen, setAddEventOpen] = useState(false);
  const [addEventSaving, setAddEventSaving] = useState(false);
  const [addEventError, setAddEventError] = useState("");
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  // Keep the full row so bank/past details still render if the list refilters.
  const [selectedEvent, setSelectedEvent] = useState<EventRow | null>(null);
  const [newEventWhere, setNewEventWhere] = useState("");
  const [newEventVenueId, setNewEventVenueId] = useState("");
  const [newEventDate, setNewEventDate] = useState("");
  const [newEventLocation, setNewEventLocation] = useState("");
  const [newEventAddress, setNewEventAddress] = useState("");
  const [newEventDoors, setNewEventDoors] = useState("");
  const [newEventShowStart, setNewEventShowStart] = useState("");
  const [newEventShowEnd, setNewEventShowEnd] = useState("");
  const [newEventPhotoUrl, setNewEventPhotoUrl] = useState("");
  const [newEventWalkthrough, setNewEventWalkthrough] = useState("");
  const [newEventCapacity, setNewEventCapacity] = useState("");
  const [newEventStatus, setNewEventStatus] = useState<"confirmed" | "hold">("confirmed");
  const [addVenueOpen, setAddVenueOpen] = useState(false);
  const [editingVenueId, setEditingVenueId] = useState<string | null>(null);
  const [venueSaving, setVenueSaving] = useState(false);
  const [venueError, setVenueError] = useState("");
  const [venueName, setVenueName] = useState("");
  const [venueAddress, setVenueAddress] = useState("");
  const [venueNeighborhood, setVenueNeighborhood] = useState("");
  const [venueMarket, setVenueMarket] = useState("NYC");
  const [venueCapacity, setVenueCapacity] = useState("");
  const [venuePricing, setVenuePricing] = useState("");
  const [venueWalkthrough, setVenueWalkthrough] = useState("");
  const [venueWhy, setVenueWhy] = useState("");
  const [venuePhotoUrl, setVenuePhotoUrl] = useState("");
  const [venueIsNew, setVenueIsNew] = useState(true);
  const [localBundle, setLocalBundle] = useState<{
    venues: Venue[];
    upcoming: EventRow[];
    past: EventRow[];
    open_dates: OpenDate[];
    markets: string[];
  } | null>(null);

  const authed = Boolean(role);
  // Full catalog for event editor / resolve helpers. Tab grids keep using `venues`.
  const venueCatalog = allVenues.length ? allVenues : venues;

  useEffect(() => {
    ensureBiltFonts();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const magic = params.get("magic");
      if (magic) {
        const res = await verifyBiltMagic(magic);
        if (res.ok && res.data.session_token) {
          storeBiltSessionToken(res.data.session_token);
          if (!cancelled) {
            setRole(res.data.role || "partner");
            setMeEmail(res.data.email || "");
          }
        } else if (!cancelled) {
          setGateError(res.error || "That sign-in link is invalid or expired.");
        }
        setParams({}, { replace: true });
      }

      const localPreview = getDomainGateSession();
      if (localPreview && !cancelled) {
        setRole(localPreview.role);
        setMeEmail(localPreview.email);
        // Domain gate alone has no Bearer token; refresh API session so comments can notify.
        void ensureBiltApiSession();
        setBooting(false);
        return;
      }

      const token = getBiltSessionToken();
      if (token) {
        const me = await biltFetch<{ email: string; role: BiltRole }>("me/");
        if (me.ok) {
          if (!cancelled) {
            setRole(me.data.role);
            setMeEmail(me.data.email);
          }
        } else if (me.status === 401) {
          clearBiltSessionToken();
        } else if (me.status === 0 && !cancelled) {
          setApiDown(true);
        }
      }
      if (!cancelled) setBooting(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [params, setParams]);

  async function loadLocalSheetPreview() {
    try {
      const res = await fetch("/bilt-portal/local-sheet-preview.json", { cache: "no-store" });
      if (!res.ok) return null;
      const data = (await res.json()) as {
        label?: string;
        venues?: Venue[];
        upcoming?: EventRow[];
        past?: EventRow[];
        open_dates?: OpenDate[];
        markets?: string[];
      };
      const venues = (data.venues || []).map(enrichVenue);
      const cleanEvents = (rows: EventRow[] = []) =>
        rows.map((ev) => ({ ...ev, description: cleanPartnerCopy(ev.description) }));
      const bundle = {
        venues,
        upcoming: cleanEvents(data.upcoming),
        past: cleanEvents(data.past),
        open_dates: data.open_dates || [],
        markets: data.markets?.length
          ? data.markets
          : collectMarkets(venues, [...(data.upcoming || []), ...(data.past || [])]),
      };
      setLocalBundle(bundle);
      setSheetPreviewLabel(data.label || "Local sheet preview");
      setMarkets(filterPortalMarkets(bundle.markets));
      setApiDown(false);
      return bundle;
    } catch {
      return null;
    }
  }

  async function refreshOverview() {
    const [ov, sync] = await Promise.all([
      biltFetch<{
        upcoming: EventRow[];
        new_venues: Venue[];
        open_dates: OpenDate[];
        updated_since_last_visit: string[];
      }>("overview/"),
      biltFetch<{ stale: boolean; last_successful_at: string | null }>("sync-status/"),
    ]);
    // Never fall back to the static sheet JSON here. That is browser-local and
    // makes partner edits look saved when other users cannot see them.
    if (ov.status === 0 || !ov.ok) {
      setApiDown(true);
      setSheetPreviewLabel(null);
      setOverview({
        upcoming: [],
        new_venues: [],
        open_dates: [],
        updated_since_last_visit: [],
      });
      return;
    }
    setApiDown(false);
    setSheetPreviewLabel(null);
    setOverview(ov.data);
    if (sync.ok) {
      setStale(Boolean(sync.data.stale));
      setLastSync(sync.data.last_successful_at);
    }
  }

  function filterLocalEvents(rows: EventRow[]) {
    return rows.filter((ev) => {
      if (!eventMatchesMarket(ev, market)) return false;
      if (programming && eventProgramming(ev, venueCatalog) !== programming) return false;
      if (query) {
        const q = query.toLowerCase();
        const hay = `${ev.title} ${ev.venue_name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }

  async function refreshEvents(scope: "upcoming" | "past" = tab === "bank" ? "past" : "upcoming") {
    const qs = new URLSearchParams();
    qs.set("scope", scope);
    if (market) qs.set("market", market);
    if (programming) qs.set("programming", programming);
    if (query) qs.set("q", query);
    const res = await biltFetch<EventRow[]>(`events/?${qs}`);
    if (!res.ok || !Array.isArray(res.data)) {
      setApiDown(true);
      if (scope === "upcoming") setEvents([]);
      return;
    }
    setApiDown(false);
    let rows = res.data;
    if (programming) {
      rows = rows.filter((ev) => eventProgramming(ev, venueCatalog) === programming);
    }
    if (scope === "upcoming") {
      rows = sortUpcoming(rows);
    }
    setEvents(rows);
  }

  function resetAddEventForm() {
    setEditingEventId(null);
    setNewEventWhere("");
    setNewEventVenueId("");
    setNewEventDate("");
    setNewEventLocation("");
    setNewEventAddress("");
    setNewEventDoors("");
    setNewEventShowStart("");
    setNewEventShowEnd("");
    setNewEventPhotoUrl("");
    setNewEventWalkthrough("");
    setNewEventCapacity("");
    setNewEventStatus("confirmed");
    setNewEventProgramming("comedy");
    setAddEventError("");
  }

  function resetVenueForm() {
    setEditingVenueId(null);
    setAddVenueOpen(false);
    setVenueName("");
    setVenueAddress("");
    setVenueNeighborhood("");
    setVenueMarket("NYC");
    setVenueProgramming("comedy");
    setVenueCapacity("");
    setVenuePricing("");
    setVenueWalkthrough("");
    setVenueWhy("");
    setVenuePhotoUrl("");
    setVenueIsNew(true);
    setVenueError("");
  }

  function openAddEventForm() {
    resetAddEventForm();
    setAddEventOpen(true);
    if (!allVenues.length) void refreshVenueCatalog();
  }

  function openEventDetail(ev: EventRow) {
    setSelectedVenue(null);
    setAddEventOpen(false);
    if (editingEventId && editingEventId !== ev.id) resetAddEventForm();
    setSelectedEvent(ev);
    setSelectedEventId(ev.id);
  }

  function closeEventDetail() {
    setSelectedEvent(null);
    setSelectedEventId(null);
    resetAddEventForm();
    setAddEventOpen(false);
  }

  function openEditEventForm(ev: EventRow) {
    const times = parseEventTimes(ev.partner_notes || "");
    const venue = resolveEventVenue(ev, venueCatalog);
    setAddEventOpen(false);
    setSelectedEvent(ev);
    setSelectedEventId(ev.id);
    setEditingEventId(ev.id);
    // Prefer show title so comedian names (e.g. Gianmarco) are not dropped on save.
    setNewEventWhere(ev.title || ev.venue_name || "");
    setNewEventVenueId(isUuid(ev.venue_id) ? ev.venue_id || "" : "");
    setNewEventDate((ev.event_date || "").slice(0, 10));
    setNewEventLocation(
      (ev.venue_neighborhood || venue?.neighborhood || "").trim()
    );
    setNewEventAddress(
      (
        (isRealStreetAddress(ev.venue_address || "") && ev.venue_address) ||
        (venue && isRealStreetAddress(venue.address || "") && venue.address) ||
        addressForVenueName(ev.venue_name || "") ||
        addressForVenueName(ev.title || "") ||
        venue?.address ||
        ""
      ).trim()
    );
    setNewEventDoors(ev.doors_time || times.doors);
    setNewEventShowStart(ev.show_start || times.start);
    setNewEventShowEnd(ev.show_end || times.end);
    setNewEventPhotoUrl(ev.photo_urls?.[0] || "");
    setNewEventWalkthrough(eventWalkthrough(ev, venueCatalog));
    setNewEventCapacity(resolveEventCapacity(ev, venueCatalog));
    setNewEventStatus(isConfirmedShow(ev) ? "confirmed" : "hold");
    setNewEventProgramming(eventProgramming(ev, venueCatalog));
    setAddEventError("");
  }

  function openAddVenueForm(asNew = true) {
    resetVenueForm();
    setVenueIsNew(asNew);
    setAddVenueOpen(true);
  }

  function openEditVenueForm(v: Venue) {
    setAddVenueOpen(false);
    setEditingVenueId(v.id);
    setSelectedVenue(v.id);
    setVenueName(v.name || "");
    setVenueAddress(v.address || "");
    setVenueNeighborhood(v.neighborhood || "");
    setVenueMarket(v.market || "NYC");
    setVenueProgramming(normalizeProgramming(v.programming));
    setVenueCapacity(v.capacity_text || "");
    setVenuePricing(v.pricing_text || "");
    setVenueWalkthrough(v.walkthrough_video_url || "");
    setVenueWhy(v.why_excited || "");
    setVenuePhotoUrl(v.photo_urls?.[0] || "");
    setVenueIsNew(Boolean(v.is_new_to_portal));
    setVenueError("");
  }

  function closeEventEditor() {
    resetAddEventForm();
    setAddEventOpen(false);
  }

  async function submitNewEvent() {
    const where = newEventWhere.trim();
    if (!where || !newEventDate) {
      setAddEventError("Where and date are required.");
      return;
    }
    const listed = venueCatalog.find((v) => v.id === newEventVenueId);
    // Keep the catalog pick even when Where is a show title (e.g. Gianmarco at Housing Works).
    const linkedVenue = listed || null;
    const linkedVenueId = isUuid(linkedVenue?.id) ? linkedVenue!.id : undefined;
    setAddEventSaving(true);
    setAddEventError("");
    const payload = {
      where,
      event_date: newEventDate,
      details: "",
      neighborhood: newEventLocation.trim(),
      address: newEventAddress.trim(),
      doors_time: newEventDoors.trim(),
      show_start: newEventShowStart.trim(),
      show_end: newEventShowEnd.trim(),
      venue_id: linkedVenueId,
      market: linkedVenue?.market || "NYC",
      programming: newEventProgramming,
      photo_url: newEventPhotoUrl.trim(),
      capacity_text: newEventCapacity.trim(),
      status: newEventStatus,
    };
    const noteBits = [
      payload.doors_time ? `Doors: ${payload.doors_time}` : "",
      payload.show_start ? `Show start: ${payload.show_start}` : "",
      payload.show_end ? `Show end: ${payload.show_end}` : "",
    ].filter(Boolean);
    const editingId = editingEventId;
    const walkthroughUrl = normalizeWalkthroughUrl(newEventWalkthrough);

    async function persistWalkthroughOnVenue(venueId: string) {
      // Never PATCH an empty walkthrough from the event form — that wiped Drive links
      // when someone later edited times without touching the video field.
      const normalized = normalizeWalkthroughUrl(walkthroughUrl);
      if (!isUuid(venueId) || !normalized) return;
      const res = await biltFetch<Venue>(`venues/${venueId}/`, {
        method: "PATCH",
        body: JSON.stringify({ walkthrough_video_url: normalized }),
      });
      if (res.ok && res.data) {
        const patchWalk = (prev: Venue[]) =>
          prev.map((v) =>
            v.id === venueId
              ? {
                  ...v,
                  walkthrough_video_url:
                    res.data.walkthrough_video_url || normalized,
                }
              : v
          );
        setVenues(patchWalk);
        setAllVenues(patchWalk);
      }
    }

    const authedApi = await ensureBiltApiSession();

    if (editingId && isUuid(editingId)) {
      if (!authedApi.ok) {
        setAddEventSaving(false);
        setAddEventError(authedApi.error || "Please sign in again, then save your changes.");
        return;
      }
      const res = await biltFetch<EventRow>(`events/${editingId}/`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      setAddEventSaving(false);
      if (res.ok && res.data) {
        if (linkedVenueId) await clearVenueNewFlag(linkedVenueId);
        if (linkedVenueId) await persistWalkthroughOnVenue(linkedVenueId);
        const savedVenueId =
          (isUuid(res.data.venue_id) ? res.data.venue_id : null) || linkedVenueId || "";
        if (savedVenueId) {
          const upsertSavedVenue = (prev: Venue[]) => {
            const hit = prev.find((v) => v.id === savedVenueId);
            const nextCap =
              (res.data.venue_capacity_text || payload.capacity_text || "").trim() ||
              hit?.capacity_text ||
              "";
            const patched: Venue = {
              ...(hit || {
                id: savedVenueId,
                name: res.data.venue_name || where,
                market: payload.market,
                neighborhood: "",
                address: "",
                capacity_text: "",
                pricing_text: "",
                walkthrough_video_url: "",
                why_excited: "",
                hosted_before: null,
                is_new_to_portal: false,
                sheet_tabs: [],
                photo_urls: [],
                show_types: [],
              }),
              neighborhood:
                payload.neighborhood ||
                res.data.venue_neighborhood ||
                hit?.neighborhood ||
                "",
              address:
                payload.address || res.data.venue_address || hit?.address || "",
              capacity_text: nextCap,
              walkthrough_video_url:
                walkthroughUrl || hit?.walkthrough_video_url || "",
            };
            return withLocalReactions([
              ...prev.filter((v) => v.id !== savedVenueId),
              patched,
            ]);
          };
          setVenues(upsertSavedVenue);
          setAllVenues(upsertSavedVenue);
        }
        setSelectedEvent(res.data);
        setEvents((prev) => mergeEventIntoList(prev, res.data));
        closeEventEditor();
        return;
      }
      setAddEventError(
        res.error ||
          (res.status === 401
            ? "Please sign in again."
            : res.status === 404
              ? "That event was not found on the server."
              : `Could not save changes (${res.status || "network"}).`)
      );
      return;
    }

    if (!editingId) {
      if (authedApi.ok) {
        const res = await biltFetch<EventRow>("events/", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        setAddEventSaving(false);
        if (res.ok && res.data) {
          if (linkedVenueId) await clearVenueNewFlag(linkedVenueId);
          if (linkedVenueId) await persistWalkthroughOnVenue(linkedVenueId);
          const savedVenueId =
            (isUuid(res.data.venue_id) ? res.data.venue_id : null) || linkedVenueId || "";
          if (savedVenueId) {
            const upsertSavedVenue = (prev: Venue[]) => {
              const hit = prev.find((v) => v.id === savedVenueId);
              const nextCap =
                (res.data.venue_capacity_text || payload.capacity_text || "").trim() ||
                hit?.capacity_text ||
                "";
              const patched: Venue = {
                ...(hit || {
                  id: savedVenueId,
                  name: res.data.venue_name || where,
                  market: payload.market,
                  neighborhood: "",
                  address: "",
                  capacity_text: "",
                  pricing_text: "",
                  walkthrough_video_url: "",
                  why_excited: "",
                  hosted_before: null,
                  is_new_to_portal: false,
                  sheet_tabs: [],
                  photo_urls: [],
                  show_types: [],
                }),
                capacity_text: nextCap,
                neighborhood:
                  payload.neighborhood ||
                  res.data.venue_neighborhood ||
                  hit?.neighborhood ||
                  "",
                address:
                  payload.address || res.data.venue_address || hit?.address || "",
                walkthrough_video_url:
                  walkthroughUrl || hit?.walkthrough_video_url || "",
              };
              return withLocalReactions([
                ...prev.filter((v) => v.id !== savedVenueId),
                patched,
              ]);
            };
            setVenues(upsertSavedVenue);
            setAllVenues(upsertSavedVenue);
          }
          setSelectedEvent(res.data);
          setEvents((prev) => mergeEventIntoList(prev, res.data));
          closeEventEditor();
          return;
        }
        // Do not sessionStorage-bank failed creates. That only shows on this browser.
        setAddEventError(
          res.error ||
            `Could not create event (${res.status || "network"}). Nothing was saved for other users.`
        );
        return;
      }
      setAddEventSaving(false);
      setAddEventError(authedApi.error || "Could not open a BILT API session.");
      return;
    }

    setAddEventSaving(false);
    setAddEventError(
      "Could not save to the shared portal. Sign in again or wait for the API, then retry. Nothing was saved for other users."
    );
  }

  async function submitVenue() {
    const name = venueName.trim();
    if (!name) {
      setVenueError("Venue name is required.");
      return;
    }
    setVenueSaving(true);
    setVenueError("");
    const payload = {
      name,
      address: venueAddress.trim(),
      neighborhood: venueNeighborhood.trim(),
      market: venueMarket.trim() || "NYC",
      programming: venueProgramming,
      capacity_text: venueCapacity.trim(),
      pricing_text: venuePricing.trim(),
      walkthrough_video_url: normalizeWalkthroughUrl(venueWalkthrough),
      why_excited: venueWhy.trim(),
      photo_url: venuePhotoUrl.trim(),
      is_new_to_portal: venueIsNew,
      placement: venueIsNew ? "new" : "bank",
    };
    const authedApi = await ensureBiltApiSession();
    const editingId = editingVenueId;

    if (editingId && isUuid(editingId)) {
      if (!authedApi.ok) {
        setVenueSaving(false);
        setVenueError(authedApi.error || "Please sign in again, then save your changes.");
        return;
      }
      const res = await biltFetch<Venue>(`venues/${editingId}/`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      setVenueSaving(false);
      if (res.ok && res.data) {
        const upsertEditedVenue = (prev: Venue[]) =>
          withLocalReactions([...prev.filter((v) => v.id !== editingId), res.data]);
        setVenues(upsertEditedVenue);
        setAllVenues(upsertEditedVenue);
        setVenueDetail((prev) => (prev ? { ...prev, venue: res.data } : prev));
        // Keep open show cards in sync when capacity/address change on the venue.
        setEvents((prev) =>
          prev.map((ev) =>
            ev.venue_id === editingId
              ? {
                  ...ev,
                  venue_name: res.data.name || ev.venue_name,
                  venue_capacity_text: res.data.capacity_text || "",
                  venue_neighborhood: res.data.neighborhood || "",
                  venue_address: res.data.address || "",
                }
              : ev
          )
        );
        setSelectedEvent((prev) =>
          prev && prev.venue_id === editingId
            ? {
                ...prev,
                venue_name: res.data.name || prev.venue_name,
                venue_capacity_text: res.data.capacity_text || "",
                venue_neighborhood: res.data.neighborhood || "",
                venue_address: res.data.address || "",
              }
            : prev
        );
        resetVenueForm();
        setSelectedVenue(editingId);
        void refreshVenues(tab === "bank" ? "inactive" : "new");
        return;
      }
      setVenueError(res.error || "Could not save venue.");
      return;
    }

    if (authedApi.ok) {
      const res = await biltFetch<Venue>("venues/", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setVenueSaving(false);
      if (res.ok && res.data) {
        resetVenueForm();
        void refreshVenues(tab === "bank" ? "inactive" : "new");
        return;
      }
      setVenueError(res.error || "Could not create venue.");
      return;
    }
    setVenueSaving(false);
    setVenueError(authedApi.error || "Could not open a BILT API session.");
  }

  async function moveEventPlacement(ev: EventRow, placement: "upcoming" | "past") {
    const today = new Date().toISOString().slice(0, 10);
    const times = parseEventTimes(ev.partner_notes || "");
    let eventDate = (ev.event_date || "").slice(0, 10);
    if (placement === "upcoming" && eventDate && eventDate < today) {
      eventDate = today;
    }
    const payload = {
      where: ev.venue_name || ev.title,
      event_date: eventDate,
      details: ev.description || "",
      doors_time: times.doors,
      show_start: times.start,
      show_end: times.end,
      venue_id: isUuid(ev.venue_id) ? ev.venue_id : undefined,
      market: ev.market || "NYC",
      photo_url: ev.photo_urls?.[0] || "",
      status: placement === "past" ? "past" : "confirmed",
    };
    const authedApi = await ensureBiltApiSession();
    if (!authedApi.ok || !isUuid(ev.id)) {
      setAddEventError(authedApi.error || "Please sign in again to move this show.");
      return;
    }
    const res = await biltFetch<EventRow>(`events/${ev.id}/`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
    if (!res.ok || !res.data) {
      setAddEventError(res.error || "Could not move this show.");
      return;
    }
    if (placement === "past") {
      setEvents((prev) => prev.filter((e) => e.id !== ev.id));
    } else {
      setEvents((prev) => mergeEventIntoList(prev, res.data));
    }
    closeEventDetail();
    if (placement === "upcoming") setTab("upcoming");
    if (placement === "past") setTab("bank");
  }

  async function moveVenuePlacement(venue: Venue, placement: "new" | "bank") {
    if (!isUuid(venue.id)) return;
    const authedApi = await ensureBiltApiSession();
    if (!authedApi.ok) return;
    const res = await biltFetch<Venue>(`venues/${venue.id}/`, {
      method: "PATCH",
      body: JSON.stringify({ placement }),
    });
    if (!res.ok || !res.data) return;
    setVenueDetail((prev) => (prev ? { ...prev, venue: res.data } : prev));
    setSelectedVenue(null);
    resetVenueForm();
    if (placement === "new") {
      setTab("venues");
      void refreshVenues("new");
    } else {
      setTab("bank");
      void refreshVenues("inactive");
    }
  }

  async function moveEventToNewVenues(ev: EventRow) {
    const authedApi = await ensureBiltApiSession();
    if (!authedApi.ok) {
      setAddEventError(authedApi.error || "Please sign in again to move this show.");
      return;
    }
    const name = (ev.venue_name || ev.title || "").trim();
    if (!name) return;

    if (isUuid(ev.venue_id)) {
      const res = await biltFetch<Venue>(`venues/${ev.venue_id}/`, {
        method: "PATCH",
        body: JSON.stringify({ placement: "new", name }),
      });
      if (!res.ok) {
        setAddEventError(res.error || "Could not move venue to New Venues.");
        return;
      }
    } else {
      const res = await biltFetch<Venue>("venues/", {
        method: "POST",
        body: JSON.stringify({
          name,
          market: ev.market || "NYC",
          photo_url: ev.photo_urls?.[0] || "",
          capacity_text: resolveEventCapacity(ev, venueCatalog),
          placement: "new",
          is_new_to_portal: true,
        }),
      });
      if (!res.ok) {
        setAddEventError(res.error || "Could not add venue to New Venues.");
        return;
      }
    }
    closeEventDetail();
    setTab("venues");
    void refreshVenues("new");
  }

  async function clearVenueNewFlag(venueId: string): Promise<boolean> {
    if (!isUuid(venueId)) return false;
    const res = await biltFetch(`venues/${venueId}/`, {
      method: "PATCH",
      body: JSON.stringify({ placement: "bank", is_new_to_portal: false }),
    });
    return Boolean(res.ok);
  }

  function eventMatchesVenue(e: EventRow, venue: Venue) {
    if (e.venue_id && venue.id && e.venue_id === venue.id) return true;
    const nameKey = (venue.name || "").trim().toLowerCase();
    const label = (e.venue_name || e.title || "")
      .trim()
      .toLowerCase()
      .replace(/^bilt\s+/, "");
    if (!nameKey || !label) return false;
    return label === nameKey || label.includes(nameKey) || nameKey.includes(label);
  }

  async function moveVenueToUpcoming(venue: Venue) {
    const authedApi = await ensureBiltApiSession();
    if (!authedApi.ok) {
      setVenueError(authedApi.error || "Please sign in again to move this venue.");
      return;
    }

    // Always load shows from the API. Tab-local `events` is often empty/stale on New Venues.
    const [upRes, pastRes] = await Promise.all([
      biltFetch<EventRow[]>("events/?scope=upcoming"),
      biltFetch<EventRow[]>("events/?scope=past"),
    ]);
    const upcomingList = upRes.ok && Array.isArray(upRes.data) ? upRes.data : [];
    const pastList = pastRes.ok && Array.isArray(pastRes.data) ? pastRes.data : [];
    const detailUpcoming = ((venueDetail?.upcoming_events as EventRow[] | undefined) || []).filter(
      Boolean
    );
    const detailPast = ((venueDetail?.past_events as EventRow[] | undefined) || []).filter(Boolean);

    const upcomingHit =
      detailUpcoming.find((e) => eventMatchesVenue(e, venue)) ||
      upcomingList.find((e) => eventMatchesVenue(e, venue)) ||
      null;
    const pastHit =
      detailPast.find((e) => eventMatchesVenue(e, venue)) ||
      pastList.find((e) => eventMatchesVenue(e, venue)) ||
      null;

    setSelectedVenue(null);
    resetVenueForm();
    setSelectedEvent(null);
    setSelectedEventId(null);
    setEditingEventId(null);
    setTab("upcoming");
    if (upcomingList.length) {
      setEvents(sortUpcoming(upcomingList));
    } else {
      void refreshEvents("upcoming");
    }

    if (upcomingHit && isUuid(upcomingHit.id)) {
      // Only leave New Venues once we know the show exists on Upcoming.
      const cleared = await clearVenueNewFlag(venue.id);
      if (!cleared) {
        setVenueError("Could not update venue placement on the shared portal. Try again.");
        setTab("venues");
        void refreshVenues("new");
        return;
      }
      setEvents((prev) => {
        if (prev.some((e) => e.id === upcomingHit.id)) return prev;
        return sortUpcoming([upcomingHit, ...prev]);
      });
      openEventDetail(upcomingHit);
      return;
    }

    // No upcoming show yet: keep the venue on New Venues until they save a date.
    // Prefill the on-page Add event form (never an empty side drawer).
    resetAddEventForm();
    setNewEventWhere(venue.name || "");
    setNewEventVenueId(isUuid(venue.id) ? venue.id : "");
    setNewEventCapacity((venue.capacity_text || "").trim());
    setNewEventPhotoUrl(venue.photo_urls?.[0] || "");
    setNewEventWalkthrough(
      (venue.walkthrough_video_url || "").trim() ||
        lookupVenueDir(VENUE_WALKTHROUGH_DIR, venue.name)
    );
    setNewEventLocation((venue.neighborhood || "").trim());
    setNewEventAddress((venue.address || "").trim());
    if (pastHit) {
      const times = parseEventTimes(pastHit.partner_notes || "");
      setNewEventDoors(times.doors);
      setNewEventShowStart(times.start);
      setNewEventShowEnd(times.end);
      const d = (pastHit.event_date || "").slice(0, 10);
      const today = new Date().toISOString().slice(0, 10);
      if (d && d >= today) setNewEventDate(d);
    }
    setAddEventOpen(true);
    setAddEventError("Set the show date, then save. That puts this venue on Upcoming Shows.");
  }

  async function deletePartnerEvent(ev: EventRow) {
    if (!isPartnerEditableEvent(ev)) return;
    const label = ev.venue_name || ev.title || "this event";
    if (!window.confirm(`Delete ${label} on ${ev.event_date}?`)) return;

    if (String(ev.id).startsWith("local-added-") || !isUuid(ev.id)) {
      removeLocalEvent(ev.id);
      setEvents((prev) => prev.filter((e) => e.id !== ev.id));
      if (editingEventId === ev.id) closeEventEditor();
      if (selectedEventId === ev.id) {
        setSelectedEvent(null);
        setSelectedEventId(null);
      }
      return;
    }

    await ensureBiltApiSession();
    const res = await biltFetch(`events/${ev.id}/`, { method: "DELETE" });
    if (res.ok || res.status === 204) {
      setEvents((prev) => prev.filter((e) => e.id !== ev.id));
      if (editingEventId === ev.id) closeEventEditor();
      if (selectedEventId === ev.id) {
        setSelectedEvent(null);
        setSelectedEventId(null);
      }
      return;
    }
    setAddEventError(res.error || "Could not delete. API needs to be up for deletes.");
    openEditEventForm(ev);
  }

  async function refreshVenueCatalog() {
    const res = await biltFetch<Venue[]>("venues/?sort=newest");
    if (!res.ok || !Array.isArray(res.data)) {
      if (res.status === 0) setApiDown(true);
      return;
    }
    setApiDown(false);
    setAllVenues(withLocalReactions(res.data));
  }

  async function refreshVenues(mode: "new" | "inactive" = tab === "bank" ? "inactive" : "new") {
    const qs = new URLSearchParams({ sort: "newest" });
    if (market) qs.set("market", market);
    if (programming) qs.set("programming", programming);
    if (query) qs.set("q", query);
    const res = await biltFetch<Venue[]>(`venues/?${qs}`);
    if (!res.ok || !Array.isArray(res.data)) {
      setApiDown(true);
      setVenues([]);
      return;
    }
    setApiDown(false);
    let rows = res.data;
    if (programming) {
      rows = rows.filter((v) => normalizeProgramming(v.programming) === programming);
    }
    const filtered =
      mode === "new"
        ? rows.filter((v) => v.is_new_to_portal)
        : rows.filter((v) => v.hosted_before !== true && !v.is_new_to_portal);
    setVenues(withLocalReactions(filtered));
  }

  useEffect(() => {
    if (!authed) return;
    const current: TabId =
      tab === "upcoming" || tab === "venues" || tab === "bank" || tab === "admin" ? tab : "upcoming";
    if (current !== tab) {
      setTab(current);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingTab(true);
      const filters = await biltFetch<{ markets: string[] }>("filters/");
      if (!cancelled) {
        if (filters.ok && filters.data.markets?.length) {
          setMarkets(filterPortalMarkets(filters.data.markets));
        }
        if ((market || "").trim().toLowerCase() === "ecosystem") {
          setMarket("");
        }
      }
      if (current === "upcoming") {
        await refreshEvents("upcoming");
        // Warm full venue catalog so Add/Edit event can search every portal room.
        if (!allVenues.length) await refreshVenueCatalog();
      }
      if (current === "venues") {
        await refreshVenues("new");
        if (!allVenues.length) await refreshVenueCatalog();
      }
      if (current === "bank") {
        await refreshEvents("past");
        await refreshVenues("inactive");
        if (!allVenues.length) await refreshVenueCatalog();
      }
      if (!cancelled) setLoadingTab(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, tab, market, programming, query]);

  useEffect(() => {
    if (!selectedVenue) {
      setVenueDetail(null);
      return;
    }
    setCommentBody("");
    setVenueError("");
    (async () => {
      const res = await biltFetch<Record<string, unknown>>(`venues/${selectedVenue}/`);
      if (res.ok && res.data) {
        const rawVenue = res.data.venue as Venue | undefined;
        setVenueDetail({
          ...res.data,
          venue: rawVenue ? enrichVenue(rawVenue) : rawVenue,
        });
        return;
      }
      setApiDown(true);
      setVenueDetail(null);
    })();
  }, [selectedVenue]);

  const groupedEvents = useMemo(() => {
    const map = new Map<string, EventRow[]>();
    for (const ev of events) {
      const key = monthKey(ev.event_date);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(ev);
    }
    return [...map.entries()];
  }, [events]);

  async function onRequestAccess(e: React.FormEvent) {
    e.preventDefault();
    setSending(true);
    setGateError("");
    setGateMessage("");
    setDebugUrl("");

    const normalized = email.trim().toLowerCase();
    if (!isAllowedBiltPortalEmail(normalized)) {
      setSending(false);
      setGateError("Use a @bilt.com, @biltrewards.com, or @comedyuo.com email.");
      return;
    }

    // Walk in immediately for allowed domains (works even when API is down).
    const role = roleForAllowedEmail(normalized);
    storeDomainGateSession(normalized, role);
    setRole(role);
    setMeEmail(normalized);

    const res = await requestBiltAccess(normalized);
    setSending(false);
    if (res.ok && res.data.session_token) {
      storeBiltSessionToken(res.data.session_token);
      setRole((res.data.role as BiltRole) || role);
      setMeEmail((res.data.email as string) || normalized);
    }
    // Domain gate lets them in; data still requires a working shared API.
  }

  async function setReaction(value: string) {
    if (!selectedVenue) return;
    const current =
      (venueDetail?.my_reaction as string | null | undefined) ||
      venues.find((v) => v.id === selectedVenue)?.my_reaction ||
      "";
    // Tap the selected chip again to clear.
    const next = current === value ? "" : value;
    bankLocalReaction(selectedVenue, next);
    setVenues((prev) =>
      prev.map((v) =>
        v.id === selectedVenue
          ? { ...v, reaction: next || undefined, my_reaction: next || null }
          : v
      )
    );
    setVenueDetail((prev) => (prev ? { ...prev, my_reaction: next || null } : prev));

    const authedApi = await ensureBiltApiSession();
    if (!authedApi.ok) {
      setVenueError(authedApi.error || "Could not save reaction to the shared portal.");
      return;
    }
    const resPut = await biltFetch("reactions/", {
      method: "PUT",
      body: JSON.stringify({
        subject_type: "venue",
        subject_id: selectedVenue,
        value: next || "clear",
      }),
    });
    if (!resPut.ok) {
      setVenueError(resPut.error || "Could not save reaction.");
      return;
    }
    setVenueError("");
    const res = await biltFetch<Record<string, unknown>>(`venues/${selectedVenue}/`);
    if (res.ok) setVenueDetail(res.data);
  }

  async function submitComment() {
    if (!selectedVenue || !commentBody.trim()) return;
    const body = commentBody.trim();

    // Comments must hit the shared API so staff and other partners see them.
    const authedApi = await ensureBiltApiSession();
    if (!authedApi.ok) {
      setVenueError(authedApi.error || "Could not open a BILT API session.");
      return;
    }
    const res = await biltFetch<VenueComment>("comments/", {
      method: "POST",
      body: JSON.stringify({
        subject_type: "venue",
        subject_id: selectedVenue,
        body,
      }),
    });
    if (!res.ok) {
      setVenueError(
        res.error || "Could not save comment to the shared portal. Nothing was saved for other users."
      );
      return;
    }
    setCommentBody("");
    setVenueError("");
    const detailRes = await biltFetch<Record<string, unknown>>(`venues/${selectedVenue}/`);
    if (detailRes.ok) setVenueDetail(detailRes.data);
  }

  async function runAdminSync() {
    setAdminMsg("Syncing…");
    const authedApi = await ensureBiltApiSession();
    if (!authedApi.ok) {
      setAdminMsg(authedApi.error || "Sign in again, then retry sync.");
      return;
    }
    const res = await biltFetch("admin/sync/", {
      method: "POST",
      body: JSON.stringify({ seed_calendar: true }),
    });
    if (res.ok) {
      setAdminMsg(
        `Sync ok. Venues ${(res.data as { venue_upserts?: number }).venue_upserts ?? 0}, events ${(res.data as { event_upserts?: number }).event_upserts ?? 0}.`
      );
      await refreshOverview();
      await refreshVenues();
      return;
    }
    setAdminMsg(res.error || `Sync failed (${res.status || "network"}). Sign out, sign back in, try again.`);
  }

  async function createInvite() {
    const res = await biltFetch("admin/invites/", {
      method: "POST",
      body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
    });
    setAdminMsg(res.ok ? `Invited ${inviteEmail}` : res.error || "Invite failed");
    if (res.ok) setInviteEmail("");
  }

  function shiftMonth(delta: number) {
    const [y, m] = monthCursor.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonthCursor(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }

  if (booting) {
    return (
      <Box sx={{ ...shellSx, display: "grid", placeItems: "center" }}>
        <CircularProgress size={28} sx={{ color: ink }} />
      </Box>
    );
  }

  if (!authed) {
    return (
      <Box sx={shellSx}>
        <Box
          sx={{
            maxWidth: 520,
            mx: "auto",
            px: 3,
            pt: { xs: 10, md: 16 },
            animation: "biltFadeUp 0.55s ease-out both",
          }}
        >
          <Box component="img" src={BILT_LOGO} alt="BILT" sx={{ ...logoSx, height: 40, mb: 3.5 }} />
          <Typography
            sx={{
              fontFamily: bodyFont,
              fontSize: "0.72rem",
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: muted,
              mb: 1.75,
            }}
          >
            Partner portal
          </Typography>
          <Typography
            sx={{
              fontFamily: displayFont,
              fontWeight: 700,
              color: ink,
              fontSize: { xs: "2.35rem", md: "3.15rem" },
              letterSpacing: "-0.04em",
              lineHeight: 1.02,
              mb: 2,
            }}
          >
            ComedyUO × BILT
          </Typography>
          <Typography
            sx={{
              fontFamily: bodyFont,
              color: muted,
              mb: 4.5,
              lineHeight: 1.65,
              fontSize: "1.05rem",
              maxWidth: 420,
            }}
          >
            Enter a @bilt.com, @biltrewards.com, or @comedyuo.com email to open the portal.
          </Typography>
          <Box component="form" onSubmit={onRequestAccess} sx={{ display: "grid", gap: 1.5 }}>
            <TextField
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@bilt.com or you@comedyuo.com"
              fullWidth
              size="small"
              sx={fieldSx}
            />
            <Button
              type="submit"
              disabled={sending}
              variant="contained"
              sx={{
                fontFamily: displayFont,
                bgcolor: ctaBlue,
                color: "#0a0e14",
                textTransform: "none",
                py: 1.2,
                fontWeight: 700,
                letterSpacing: "-0.01em",
                fontSize: "0.95rem",
                borderRadius: "999px",
                transition: "transform 160ms ease, background-color 160ms ease",
                "&:hover": { bgcolor: "#b8def5", transform: "translateY(-1px)" },
              }}
            >
              {sending ? "Opening…" : "Open portal"}
            </Button>
          </Box>
          {gateMessage && (
            <Typography sx={{ mt: 2, color: muted, fontSize: "0.9rem", fontFamily: bodyFont }}>{gateMessage}</Typography>
          )}
          {gateError && (
            <Typography sx={{ mt: 2, color: "#b42318", fontSize: "0.9rem", fontFamily: bodyFont }}>{gateError}</Typography>
          )}
          {debugUrl && (
            <Typography sx={{ mt: 2, fontSize: "0.8rem", color: ink, fontFamily: bodyFont }}>
              Dev link:{" "}
              <Box component="a" href={debugUrl} sx={{ color: ink }}>
                open portal
              </Box>
            </Typography>
          )}
          {apiDown && (
            <Typography sx={{ mt: 3, fontSize: "0.85rem", color: muted, fontFamily: bodyFont }}>
              The portal API is not reachable yet. After migrate and API start, refresh this page.
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  // Partner-facing only. Staff admin UI is ?admin=1 (never shown to BILT).
  const showStaffAdmin = params.get("admin") === "1" && isComedyuoEmail(meEmail);
  const tabs: { id: TabId; label: string }[] = [
    { id: "upcoming", label: "Upcoming Shows" },
    { id: "venues", label: "New Venues" },
    { id: "bank", label: "Venue & Show Bank" },
    ...(showStaffAdmin ? [{ id: "admin" as const, label: "Admin" }] : []),
  ];
  const activeTab: TabId =
    tab === "admin" && !showStaffAdmin
      ? "upcoming"
      : tab === "upcoming" || tab === "venues" || tab === "bank" || tab === "admin"
        ? tab
        : "upcoming";

  return (
    <Box sx={shellSx}>
      <Box
        component="header"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          bgcolor: "rgba(7,9,13,0.92)",
          backdropFilter: "blur(18px) saturate(1.2)",
          WebkitBackdropFilter: "blur(18px) saturate(1.2)",
          borderBottom: `1px solid ${mist}`,
        }}
      >
        {/* One bar only: logo + tabs + actions share the same vertical center. */}
        <Box
          sx={{
            maxWidth: 1120,
            mx: "auto",
            px: { xs: 2, md: 3 },
            height: { xs: 64, md: 68 },
            display: "grid",
            gridTemplateColumns: {
              xs: "auto 1fr auto",
              md: "auto 1fr auto",
            },
            alignItems: "center",
            columnGap: { xs: 1.25, md: 2 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1.25,
              height: "100%",
              minWidth: 0,
            }}
          >
            <Box
              component="img"
              src={BILT_LOGO}
              alt="BILT"
              sx={{
                ...logoSx,
                height: 24,
                width: "auto",
                display: "block",
                flexShrink: 0,
              }}
            />
            <Box
              component="span"
              sx={{
                fontFamily: displayFont,
                fontSize: "0.88rem",
                color: ink,
                fontWeight: 600,
                letterSpacing: "-0.02em",
                lineHeight: 1,
                whiteSpace: "nowrap",
              }}
            >
              ComedyUO × BILT
            </Box>
          </Box>

          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              justifyContent: "center",
              minWidth: 0,
              height: "100%",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.35,
                maxWidth: "100%",
                overflowX: "auto",
                p: 0.35,
                borderRadius: "999px",
                bgcolor: "rgba(255,255,255,0.06)",
                border: `1px solid ${mist}`,
              }}
            >
              {tabs.map((t) => (
                <Button
                  key={t.id}
                  onClick={() => startTransition(() => setTab(t.id))}
                  sx={{
                    textTransform: "none",
                    fontFamily: bodyFont,
                    color: activeTab === t.id ? ink : muted,
                    fontWeight: activeTab === t.id ? 700 : 500,
                    bgcolor: activeTab === t.id ? "rgba(255,255,255,0.12)" : "transparent",
                    borderRadius: "999px",
                    px: 1.75,
                    py: 0.65,
                    minWidth: "auto",
                    height: 34,
                    whiteSpace: "nowrap",
                    "&:hover": { color: ink, bgcolor: "rgba(255,255,255,0.1)" },
                  }}
                >
                  {t.label}
                </Button>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1.25,
              height: "100%",
            }}
          >
            <Box
              component="span"
              sx={{
                fontFamily: bodyFont,
                fontSize: "0.75rem",
                color: muted,
                lineHeight: 1,
                display: { xs: "none", md: "inline" },
              }}
            >
              {meEmail}
            </Box>
            <Button
              size="small"
              onClick={() => {
                clearBiltSessionToken();
                setRole(null);
              }}
              sx={{
                textTransform: "none",
                color: "#0a0e14",
                bgcolor: "#fff",
                borderRadius: "999px",
                px: 1.5,
                height: 34,
                minWidth: "auto",
                fontWeight: 600,
                lineHeight: 1,
                "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
              }}
            >
              Sign out
            </Button>
          </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1120, mx: "auto", px: { xs: 2, md: 3 }, py: { xs: 3, md: 4 }, animation: "biltFadeUp 0.4s ease-out both" }}>
        {apiDown && (
          <Box sx={{ mb: 2.5, p: 1.5, ...glassCardSx, borderRadius: 1, fontSize: "0.9rem", color: panelInk }}>
            Shared portal is unavailable. Creates, edits, and moves will not sync for other people until this is fixed.
            {showStaffAdmin
              ? " Run migrate 0126 on cuo-cmd-service, then refresh."
              : " Refresh in a few minutes, or ask ComedyUO staff."}
          </Box>
        )}
        {showStaffAdmin && stale && (
          <Box sx={{ mb: 2.5, p: 1.5, ...glassCardSx, borderRadius: 1, fontSize: "0.85rem", color: panelMuted }}>
            Showing the last successful sync{lastSync ? ` from ${new Date(lastSync).toLocaleString()}` : ""}. A later refresh failed.
          </Box>
        )}

        {/* Mobile: larger scrollable tabs beside the market filter (header tabs are desktop-only). */}
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            alignItems: "center",
            gap: 1,
            mb: 2,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              p: 0.5,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.06)",
              border: `1px solid ${mist}`,
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {tabs.map((t) => (
              <Button
                key={t.id}
                onClick={() => startTransition(() => setTab(t.id))}
                sx={{
                  textTransform: "none",
                  fontFamily: bodyFont,
                  fontSize: "0.92rem",
                  color: activeTab === t.id ? "#0a0e14" : ink,
                  fontWeight: activeTab === t.id ? 700 : 550,
                  bgcolor: activeTab === t.id ? accent : "transparent",
                  borderRadius: 1.5,
                  px: 1.75,
                  py: 1.1,
                  minWidth: "auto",
                  minHeight: 44,
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                  "&:hover": {
                    color: activeTab === t.id ? "#0a0e14" : ink,
                    bgcolor: activeTab === t.id ? accent : "rgba(255,255,255,0.1)",
                  },
                }}
              >
                {t.label}
              </Button>
            ))}
          </Box>
          {(activeTab === "upcoming" || activeTab === "venues" || activeTab === "bank") && (
            <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
              <TextField
                select
                size="small"
                value={market}
                onChange={(e) => setMarket(e.target.value)}
                SelectProps={{ native: true }}
                sx={{
                  minWidth: 108,
                  maxWidth: 128,
                  ...fieldSx,
                  "& .MuiInputBase-root": { minHeight: 44 },
                }}
              >
                <option value="">All markets</option>
                {markets.map((m) => (
                  <option key={m} value={m}>
                    {marketLabel(m)}
                  </option>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                value={programming}
                onChange={(e) =>
                  setProgramming(e.target.value === "magic" ? "magic" : e.target.value === "comedy" ? "comedy" : "")
                }
                SelectProps={{ native: true }}
                sx={{
                  minWidth: 108,
                  maxWidth: 128,
                  ...fieldSx,
                  "& .MuiInputBase-root": { minHeight: 44 },
                }}
              >
                <option value="">All shows</option>
                <option value="comedy">Comedy</option>
                <option value="magic">Magic</option>
              </TextField>
            </Box>
          )}
        </Box>

        {(activeTab === "upcoming" || activeTab === "venues" || activeTab === "bank") && (
          <Box
            sx={{
              display: {
                // Mobile upcoming already has filters beside tabs; hide empty row.
                xs: activeTab === "upcoming" && !market && !programming && !query ? "none" : "flex",
                md: "flex",
              },
              flexWrap: "wrap",
              gap: 1,
              mb: 3,
              alignItems: "center",
            }}
          >
            {(activeTab === "venues" || activeTab === "bank") && (
              <TextField
                size="small"
                placeholder={activeTab === "bank" ? "Search shows or venues" : "Search venues"}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                sx={{ minWidth: 180, ...fieldSx }}
              />
            )}
            <TextField
              select
              size="small"
              value={market}
              onChange={(e) => setMarket(e.target.value)}
              SelectProps={{ native: true }}
              sx={{
                display: { xs: "none", md: "inline-flex" },
                minWidth: 160,
                ...fieldSx,
              }}
            >
              <option value="">All markets</option>
              {markets.map((m) => (
                <option key={m} value={m}>
                  {marketLabel(m)}
                </option>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              value={programming}
              onChange={(e) =>
                setProgramming(e.target.value === "magic" ? "magic" : e.target.value === "comedy" ? "comedy" : "")
              }
              SelectProps={{ native: true }}
              sx={{
                display: { xs: "none", md: "inline-flex" },
                minWidth: 140,
                ...fieldSx,
              }}
            >
              <option value="">All shows</option>
              <option value="comedy">Comedy</option>
              <option value="magic">Magic</option>
            </TextField>
            {(market || programming || query) && (
              <Button
                size="small"
                onClick={() => {
                  setMarket("");
                  setProgramming("");
                  setQuery("");
                }}
                sx={{ textTransform: "none", color: ink }}
              >
                Clear filters
              </Button>
            )}
          </Box>
        )}

        {loadingTab && (
          <Box sx={{ py: 6, display: "grid", placeItems: "center" }}>
            <CircularProgress size={24} sx={{ color: ink }} />
          </Box>
        )}

        {!loadingTab && activeTab === "upcoming" && (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
                mb: 0.75,
              }}
            >
              <Typography
                sx={{
                  fontFamily: displayFont,
                  fontWeight: 700,
                  color: ink,
                  fontSize: { xs: "1.6rem", md: "2rem" },
                  letterSpacing: "-0.03em",
                }}
              >
                Upcoming Shows
              </Typography>
              <Button
                onClick={() => {
                  if (addEventOpen || editingEventId) {
                    closeEventEditor();
                  } else {
                    openAddEventForm();
                  }
                }}
                sx={{
                  textTransform: "none",
                  fontFamily: bodyFont,
                  fontWeight: 700,
                  color: "#0a0e14",
                  bgcolor: accent,
                  borderRadius: "999px",
                  px: 1.6,
                  py: 0.7,
                  minWidth: 0,
                  "&:hover": { bgcolor: "#d4b87a" },
                }}
              >
                {addEventOpen || editingEventId ? "Close" : "+ Add event"}
              </Button>
            </Box>
            <Typography sx={{ fontFamily: bodyFont, color: muted, mb: 3, maxWidth: 560 }}>
              Upcoming confirmed nights stay in color; anything not confirmed is greyscale. Click a show for details, then edit if you need to.
            </Typography>

            {addEventOpen && !editingEventId && (
              <EventEditorPanel
                mode="add"
                venueCatalog={venueCatalog}
                where={newEventWhere}
                setWhere={setNewEventWhere}
                venueId={newEventVenueId}
                setVenueId={setNewEventVenueId}
                date={newEventDate}
                setDate={setNewEventDate}
                location={newEventLocation}
                setLocation={setNewEventLocation}
                address={newEventAddress}
                setAddress={setNewEventAddress}
                doors={newEventDoors}
                setDoors={setNewEventDoors}
                showStart={newEventShowStart}
                setShowStart={setNewEventShowStart}
                showEnd={newEventShowEnd}
                setShowEnd={setNewEventShowEnd}
                photoUrl={newEventPhotoUrl}
                setPhotoUrl={setNewEventPhotoUrl}
                walkthrough={newEventWalkthrough}
                setWalkthrough={setNewEventWalkthrough}
                capacity={newEventCapacity}
                setCapacity={setNewEventCapacity}
                status={newEventStatus}
                setStatus={setNewEventStatus}
                programming={newEventProgramming}
                setProgramming={setNewEventProgramming}
                error={addEventError}
                saving={addEventSaving}
                onSave={submitNewEvent}
                onCancel={closeEventEditor}
                sx={{ mb: 3, maxWidth: 560 }}
              />
            )}

            {events.length === 0 ? (
              <Empty text="No upcoming shows yet." />
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "1fr 1fr 1fr",
                  },
                  gap: { xs: 2, md: 2.5 },
                  alignItems: "start",
                }}
              >
                {events.map((ev) => (
                  <UpcomingShowPanel
                    key={ev.id}
                    ev={ev}
                    venues={venueCatalog}
                    active={selectedEventId === ev.id}
                    onOpen={() => openEventDetail(ev)}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {!loadingTab && activeTab === "venues" && (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
                mb: 0.75,
              }}
            >
              <Typography
                sx={{
                  fontFamily: displayFont,
                  fontWeight: 700,
                  color: ink,
                  fontSize: { xs: "1.6rem", md: "2rem" },
                  letterSpacing: "-0.03em",
                }}
              >
                New Venues
              </Typography>
              <Button
                onClick={() => {
                  if (addVenueOpen || editingVenueId) resetVenueForm();
                  else openAddVenueForm(true);
                }}
                sx={{
                  textTransform: "none",
                  fontFamily: bodyFont,
                  fontWeight: 700,
                  color: "#0a0e14",
                  bgcolor: accent,
                  borderRadius: "999px",
                  px: 1.6,
                  py: 0.7,
                  minWidth: 0,
                  "&:hover": { bgcolor: "#d4b87a" },
                }}
              >
                {addVenueOpen || editingVenueId ? "Close" : "+ Add venue"}
              </Button>
            </Box>
            <Typography sx={{ fontFamily: bodyFont, color: muted, mb: 3, maxWidth: 560 }}>
              Fresh additions we want you to look at first. Add a room anytime, with address, pricing, photo, and walkthrough.
            </Typography>
            {addVenueOpen && !editingVenueId && (
              <VenueEditorPanel
                mode="add"
                name={venueName}
                setName={setVenueName}
                address={venueAddress}
                setAddress={setVenueAddress}
                neighborhood={venueNeighborhood}
                setNeighborhood={setVenueNeighborhood}
                market={venueMarket}
                setMarket={setVenueMarket}
                programming={venueProgramming}
                setProgramming={setVenueProgramming}
                capacity={venueCapacity}
                setCapacity={setVenueCapacity}
                pricing={venuePricing}
                setPricing={setVenuePricing}
                walkthrough={venueWalkthrough}
                setWalkthrough={setVenueWalkthrough}
                why={venueWhy}
                setWhy={setVenueWhy}
                photoUrl={venuePhotoUrl}
                setPhotoUrl={setVenuePhotoUrl}
                isNew={venueIsNew}
                setIsNew={setVenueIsNew}
                showWalkthrough
                error={venueError}
                saving={venueSaving}
                onSave={submitVenue}
                onCancel={resetVenueForm}
                sx={{ mb: 3, maxWidth: 560 }}
              />
            )}
            {venues.length === 0 ? (
              <Empty text="No new venues match these filters." />
            ) : (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: 2 }}>
                {venues.map((v) => (
                  <VenueCard
                    key={v.id}
                    venue={v}
                    badge="new"
                    onOpen={() => {
                      resetVenueForm();
                      setSelectedVenue(v.id);
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {!loadingTab && activeTab === "bank" && (
          <Box sx={{ display: "grid", gap: 5 }}>
            <Box>
              <Typography
                sx={{
                  fontFamily: displayFont,
                  fontWeight: 700,
                  color: ink,
                  fontSize: { xs: "1.6rem", md: "2rem" },
                  letterSpacing: "-0.03em",
                  mb: 0.75,
                }}
              >
                Venue & Show Bank
              </Typography>
              <Typography sx={{ fontFamily: bodyFont, color: muted, mb: 3, maxWidth: 620 }}>
                Past BILT nights we&apos;ve already produced, plus older roster rooms. Click a show or venue for details and edits.
              </Typography>
            </Box>

            <Section title="Past events" subtitle="Shows already on the books, grouped by year. Click one to view or edit.">
              {events.length === 0 ? (
                <Empty text="No past BILT events yet." />
              ) : (
                <Box sx={{ display: "grid", gap: 3 }}>
                  {groupEventsByYear(events).map(([year, yearEvents]) => (
                    <Box key={year} sx={{ display: "grid", gap: 1.25 }}>
                      <Typography
                        sx={{
                          fontFamily: displayFont,
                          fontWeight: 700,
                          color: panelInk,
                          fontSize: "1.05rem",
                          letterSpacing: "-0.02em",
                        }}
                      >
                        {year}
                      </Typography>
                      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
                        {yearEvents.map((ev) => {
                          const thumb = eventImage(ev, venueCatalog);
                          const brandThumb = isEventBrandMark(thumb);
                          return (
                          <Box
                            key={ev.id}
                            component="button"
                            onClick={() => openEventDetail(ev)}
                            sx={{
                              ...glassCardSx,
                              display: "grid",
                              gridTemplateColumns: "88px 1fr",
                              gap: 1.5,
                              p: 1.25,
                              borderRadius: 2,
                              textAlign: "left",
                              cursor: "pointer",
                              outline: selectedEventId === ev.id ? "1px solid rgba(168,212,240,0.55)" : "none",
                              "&:hover": {
                                borderColor: "rgba(255,255,255,0.28)",
                                transform: "translateY(-2px)",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                borderRadius: 1.5,
                                overflow: "hidden",
                                bgcolor: "#000",
                                backgroundImage: `url(${thumb})`,
                                backgroundSize: brandThumb ? "55%" : "cover",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: brandThumb
                                  ? "center"
                                  : venuePhotoPosition(ev.venue_name || ev.title),
                                minHeight: 88,
                              }}
                            />
                            <Box sx={{ py: 0.25 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mb: 0.25 }}>
                                <Typography sx={{ fontFamily: displayFont, fontWeight: 700, color: panelInk, letterSpacing: "-0.02em" }}>
                                  {formatDate(ev.event_date)}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontFamily: bodyFont,
                                    fontSize: "0.62rem",
                                    letterSpacing: "0.1em",
                                    textTransform: "uppercase",
                                    fontWeight: 700,
                                    color:
                                      eventProgramming(ev, venueCatalog) === "magic"
                                        ? "#f5e6ff"
                                        : "#0a0e14",
                                    bgcolor:
                                      eventProgramming(ev, venueCatalog) === "magic"
                                        ? "rgba(88, 48, 120, 0.92)"
                                        : accent,
                                    borderRadius: "999px",
                                    px: 0.9,
                                    py: 0.3,
                                  }}
                                >
                                  {programmingLabel(eventProgramming(ev, venueCatalog))}
                                </Typography>
                              </Box>
                              <Typography sx={{ fontFamily: bodyFont, color: panelInk, fontWeight: 500 }}>
                                {ev.venue_name || ev.title}
                              </Typography>
                              <Typography sx={{ fontFamily: bodyFont, color: panelMuted, fontSize: "0.85rem" }}>
                                {shortLocation(ev, venueCatalog)} · {capacityLabel(resolveEventCapacity(ev, venueCatalog))}
                              </Typography>
                              <Typography sx={{ fontFamily: bodyFont, color: panelMuted, fontSize: "0.8rem" }}>
                                {isFriendsShow(ev) ? "BILT and Friends" : "BILT Neighborhood Comedy"}
                              </Typography>
                            </Box>
                          </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Section>

            <Section
              title="Not activated yet"
              subtitle="Already in the roster, but never used for a BILT show. Separate from the New Venues tab."
            >
              {venues.length === 0 ? (
                <Empty text="No inactive venues match these filters." />
              ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: 2 }}>
                  {venues.map((v) => (
                    <VenueCard
                      key={v.id}
                      venue={v}
                      badge="not_activated"
                      onOpen={() => {
                        resetVenueForm();
                        setSelectedVenue(v.id);
                      }}
                    />
                  ))}
                </Box>
              )}
            </Section>
          </Box>
        )}

        {!loadingTab && activeTab === "admin" && showStaffAdmin && (
          <Box sx={{ display: "grid", gap: 3, maxWidth: 560 }}>
            <Section title="Sheet refresh" subtitle="Read-only venue sync from the BILT venue menu. Failed refreshes keep last good data.">
              <Button
                variant="contained"
                onClick={runAdminSync}
                sx={{
                  bgcolor: ctaBlue,
                  color: "#0a0e14",
                  textTransform: "none",
                  alignSelf: "start",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#b8def5" },
                }}
              >
                Refresh venues from sheet
              </Button>
            </Section>
            <Section title="Invite teammate" subtitle="Only invited emails can request a magic link.">
              <Box sx={{ display: "grid", gap: 1.25 }}>
                <TextField size="small" placeholder="email@bilt.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} sx={fieldSx} />
                <TextField select size="small" value={inviteRole} onChange={(e) => setInviteRole(e.target.value as BiltRole)} SelectProps={{ native: true }} sx={fieldSx}>
                  <option value="partner">Partner</option>
                  <option value="admin">Admin</option>
                </TextField>
                <Button onClick={createInvite} sx={{ textTransform: "none", justifySelf: "start" }}>
                  Create invite
                </Button>
              </Box>
            </Section>
            {adminMsg && <Typography sx={{ color: muted, fontSize: "0.9rem" }}>{adminMsg}</Typography>}
          </Box>
        )}
      </Box>

      <Drawer
        anchor="right"
        open={Boolean(selectedVenue)}
        onClose={() => {
          setSelectedVenue(null);
          resetVenueForm();
        }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 440 },
            p: 2.5,
            ...glassCardSx,
            borderRadius: 0,
            borderLeft: `1px solid ${mist}`,
            bgcolor: "rgba(10,14,20,0.94)",
            color: "#ffffff",
            "& .MuiTypography-root": { color: "#ffffff" },
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Button
            onClick={() => {
              if (editingVenueId) {
                setEditingVenueId(null);
                setVenueError("");
                return;
              }
              setSelectedVenue(null);
              resetVenueForm();
            }}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: "#0a0e14",
              bgcolor: ctaBlue,
              borderRadius: "999px",
              px: 1.75,
              minWidth: 0,
              "&:hover": { bgcolor: "#b8def5" },
            }}
          >
            ← Back
          </Button>
          <Typography sx={{ fontWeight: 600, color: "#ffffff", flex: 1 }}>
            {editingVenueId ? "Edit venue" : "Venue"}
          </Typography>
        </Box>
        {editingVenueId && selectedVenue === editingVenueId ? (
          <VenueEditorPanel
            mode="edit"
            name={venueName}
            setName={setVenueName}
            address={venueAddress}
            setAddress={setVenueAddress}
            neighborhood={venueNeighborhood}
            setNeighborhood={setVenueNeighborhood}
            market={venueMarket}
            setMarket={setVenueMarket}
            programming={venueProgramming}
            setProgramming={setVenueProgramming}
            capacity={venueCapacity}
            setCapacity={setVenueCapacity}
            pricing={venuePricing}
            setPricing={setVenuePricing}
            walkthrough={venueWalkthrough}
            setWalkthrough={setVenueWalkthrough}
            why={venueWhy}
            setWhy={setVenueWhy}
            photoUrl={venuePhotoUrl}
            setPhotoUrl={setVenuePhotoUrl}
            isNew={venueIsNew}
            setIsNew={setVenueIsNew}
            showWalkthrough
            error={venueError}
            saving={venueSaving}
            onSave={submitVenue}
            onCancel={() => {
              setEditingVenueId(null);
              setVenueError("");
            }}
          />
        ) : (
          venueDetail && (
            <VenueDetail
              detail={venueDetail}
              commentBody={commentBody}
              setCommentBody={setCommentBody}
              commentError={venueError}
              onReact={setReaction}
              onComment={submitComment}
              onEdit={() => {
                const v = venueDetail.venue as Venue;
                if (v) openEditVenueForm(v);
              }}
              onMoveToNew={() => {
                const v = venueDetail.venue as Venue;
                if (v) void moveVenuePlacement(v, "new");
              }}
              onMoveToBank={() => {
                const v = venueDetail.venue as Venue;
                if (v) void moveVenuePlacement(v, "bank");
              }}
              onMoveToUpcoming={() => {
                const v = venueDetail.venue as Venue;
                if (v) void moveVenueToUpcoming(v);
              }}
            />
          )
        )}
      </Drawer>

      <Drawer
        anchor="right"
        open={Boolean(selectedEventId)}
        onClose={closeEventDetail}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 440 },
            p: 2.5,
            ...glassCardSx,
            borderRadius: 0,
            borderLeft: `1px solid ${mist}`,
            bgcolor: "rgba(10,14,20,0.94)",
            color: "#ffffff",
            "& .MuiTypography-root": { color: "#ffffff" },
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Button
            onClick={() => {
              if (editingEventId && editingEventId === selectedEventId) {
                closeEventEditor();
                return;
              }
              closeEventDetail();
            }}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: "#0a0e14",
              bgcolor: ctaBlue,
              borderRadius: "999px",
              px: 1.75,
              minWidth: 0,
              "&:hover": { bgcolor: "#b8def5" },
            }}
          >
            ← Back
          </Button>
          <Typography sx={{ fontWeight: 600, color: "#ffffff", flex: 1 }}>
            {editingEventId && editingEventId === selectedEventId ? "Edit event" : "Show details"}
          </Typography>
        </Box>
        {(() => {
          const detailEv =
            events.find((e) => e.id === selectedEventId) ||
            (selectedEvent?.id === selectedEventId ? selectedEvent : null);
          if (!detailEv) {
            return (
              <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.95rem", lineHeight: 1.5 }}>
                That show isn&apos;t loaded in this tab yet. Close this panel and open it from Upcoming
                Shows or Venue &amp; Show Bank, or use Add event if you still need to set the date.
              </Typography>
            );
          }
          if (editingEventId === detailEv.id) {
            return (
              <EventEditorPanel
                mode="edit"
                venueCatalog={venueCatalog}
                where={newEventWhere}
                setWhere={setNewEventWhere}
                venueId={newEventVenueId}
                setVenueId={setNewEventVenueId}
                date={newEventDate}
                setDate={setNewEventDate}
                location={newEventLocation}
                setLocation={setNewEventLocation}
                address={newEventAddress}
                setAddress={setNewEventAddress}
                doors={newEventDoors}
                setDoors={setNewEventDoors}
                showStart={newEventShowStart}
                setShowStart={setNewEventShowStart}
                showEnd={newEventShowEnd}
                setShowEnd={setNewEventShowEnd}
                photoUrl={newEventPhotoUrl}
                setPhotoUrl={setNewEventPhotoUrl}
                walkthrough={newEventWalkthrough}
                setWalkthrough={setNewEventWalkthrough}
                capacity={newEventCapacity}
                setCapacity={setNewEventCapacity}
                status={newEventStatus}
                setStatus={setNewEventStatus}
                programming={newEventProgramming}
                setProgramming={setNewEventProgramming}
                error={addEventError}
                saving={addEventSaving}
                onSave={submitNewEvent}
                onCancel={closeEventEditor}
                onDelete={() => void deletePartnerEvent(detailEv)}
              />
            );
          }
          return (
            <EventDetail
              ev={detailEv}
              venues={venueCatalog}
              canEdit={isPartnerEditableEvent(detailEv)}
              onEdit={() => openEditEventForm(detailEv)}
              onDelete={() => void deletePartnerEvent(detailEv)}
              onMoveToUpcoming={() => void moveEventPlacement(detailEv, "upcoming")}
              onMoveToPast={() => void moveEventPlacement(detailEv, "past")}
              onMoveToNewVenues={() => void moveEventToNewVenues(detailEv)}
              onUpdated={(next) => {
                setSelectedEvent(next);
                setEvents((prev) => mergeEventIntoList(prev, next));
              }}
            />
          );
        })()}
      </Drawer>
    </Box>
  );
}


function FileUploadField({
  label,
  accept,
  hint,
  buttonLabel,
  value,
  setValue,
}: {
  label: string;
  accept: string;
  hint: string;
  buttonLabel: string;
  value: string;
  setValue: (v: string) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");
  return (
    <Box
      sx={{
        display: "grid",
        gap: 0.75,
        p: 1.25,
        borderRadius: 2,
        border: `1px solid ${mist}`,
        bgcolor: "rgba(255,255,255,0.03)",
      }}
    >
      <Typography
        sx={{
          fontFamily: bodyFont,
          fontSize: "0.72rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: panelMuted,
        }}
      >
        {label}
      </Typography>
      <TextField
        size="small"
        placeholder="Paste a URL, or upload below"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => {
          const next = normalizeWalkthroughUrl(value);
          if (next !== value) setValue(next);
        }}
        InputLabelProps={{ shrink: true }}
        sx={fieldSx}
      />
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
        <Button
          component="label"
          disabled={uploading}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            color: "#0a0e14",
            bgcolor: ctaBlue,
            borderRadius: "999px",
            px: 1.75,
            "&:hover": { bgcolor: "#b8def5" },
          }}
        >
          {uploading ? "Uploading…" : buttonLabel}
          <input
            hidden
            type="file"
            accept={accept}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (!file) return;
              setUploading(true);
              setMsg("");
              void uploadBiltFile(file).then((res) => {
                setUploading(false);
                if (res.url) {
                  setValue(res.url);
                  setMsg("Uploaded.");
                } else {
                  setMsg(res.error || "Upload failed. Sign in again if needed.");
                }
              });
            }}
          />
        </Button>
        <Typography sx={{ fontFamily: bodyFont, color: panelMuted, fontSize: "0.78rem" }}>{hint}</Typography>
        {msg && (
          <Typography sx={{ fontFamily: bodyFont, fontSize: "0.78rem", color: msg === "Uploaded." ? accent : "#ff8a80" }}>
            {msg}
          </Typography>
        )}
      </Box>
      {value.trim() && isImageUrl(value) && (
        <Box
          component="img"
          src={value}
          alt=""
          sx={{
            width: "100%",
            maxHeight: 180,
            objectFit: "cover",
            borderRadius: 1.5,
            border: `1px solid ${mist}`,
          }}
        />
      )}
      {value.trim() && isVideoUrl(value) && (
        <Box
          component="video"
          src={value}
          controls
          playsInline
          sx={{
            width: "100%",
            maxHeight: 220,
            borderRadius: 1.5,
            border: `1px solid ${mist}`,
            bgcolor: "#000",
          }}
        />
      )}
      {value.trim() && isGoogleDriveWalkthroughUrl(value) && (() => {
        const preview = googleDrivePreviewUrl(value);
        return preview ? (
          <Box
            component="iframe"
            title="Walkthrough preview"
            src={preview}
            sx={{
              width: "100%",
              height: 220,
              border: `1px solid ${mist}`,
              borderRadius: 1.5,
            }}
            allow="autoplay"
          />
        ) : null;
      })()}
      {value.trim() && !isImageUrl(value) && !isVideoUrl(value) && (
        <Button
          href={normalizeWalkthroughUrl(value) || value}
          target="_blank"
          rel="noreferrer"
          sx={{ textTransform: "none", color: ctaBlue, justifySelf: "start", px: 0 }}
        >
          {isGoogleDriveWalkthroughUrl(value) ? "Open Google Drive link" : "Open file"}
        </Button>
      )}
    </Box>
  );
}

function EventEditorPanel({
  mode,
  venueCatalog,
  where,
  setWhere,
  venueId,
  setVenueId,
  date,
  setDate,
  location,
  setLocation,
  address,
  setAddress,
  doors,
  setDoors,
  showStart,
  setShowStart,
  showEnd,
  setShowEnd,
  photoUrl,
  setPhotoUrl,
  walkthrough,
  setWalkthrough,
  capacity,
  setCapacity,
  status,
  setStatus,
  programming,
  setProgramming,
  error,
  saving,
  onSave,
  onCancel,
  onDelete,
  sx,
}: {
  mode: "add" | "edit";
  venueCatalog: Venue[];
  where: string;
  setWhere: (v: string) => void;
  venueId: string;
  setVenueId: (v: string) => void;
  date: string;
  setDate: (v: string) => void;
  location: string;
  setLocation: (v: string) => void;
  address: string;
  setAddress: (v: string) => void;
  doors: string;
  setDoors: (v: string) => void;
  showStart: string;
  setShowStart: (v: string) => void;
  showEnd: string;
  setShowEnd: (v: string) => void;
  photoUrl: string;
  setPhotoUrl: (v: string) => void;
  walkthrough: string;
  setWalkthrough: (v: string) => void;
  capacity: string;
  setCapacity: (v: string) => void;
  status: "confirmed" | "hold";
  setStatus: (v: "confirmed" | "hold") => void;
  programming: ProgrammingKind;
  setProgramming: (v: ProgrammingKind) => void;
  error: string;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  sx?: Record<string, unknown>;
}) {
  return (
    <Box
      sx={{
        ...glassCardSx,
        borderRadius: 2,
        p: { xs: 2, md: 2.25 },
        display: "grid",
        gap: 1.25,
        borderColor: "rgba(168,212,240,0.35)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.1), 0 18px 48px rgba(0,0,0,0.45)",
        ...sx,
      }}
    >
      <Typography sx={{ fontFamily: displayFont, fontWeight: 700, color: panelInk, letterSpacing: "-0.02em" }}>
        {mode === "edit" ? "Edit event" : "Add event"}
      </Typography>
      <Typography sx={{ fontFamily: bodyFont, color: panelMuted, fontSize: "0.88rem" }}>
        {mode === "edit"
          ? "Update this night, then save. ComedyUO gets an email on save or delete."
          : "Lock in a night. Search any portal venue, or type a custom name above the list. Date is required; times are optional."}
      </Typography>
      <TextField
        size="small"
        label="Where"
        placeholder="Type any venue or room name"
        value={where}
        onChange={(e) => {
          const next = e.target.value;
          setWhere(next);
          const listed = venueCatalog.find((v) => v.id === venueId);
          if (listed && listed.name.trim().toLowerCase() !== next.trim().toLowerCase()) {
            setVenueId("");
          }
        }}
        InputLabelProps={{ shrink: true }}
        sx={fieldSx}
      />
      <Autocomplete
        options={venueCatalog.slice().sort((a, b) => a.name.localeCompare(b.name))}
        getOptionLabel={(v) => v.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        value={venueCatalog.find((v) => v.id === venueId) || null}
        onChange={(_, match) => {
          if (!match) {
            setVenueId("");
            return;
          }
          setVenueId(match.id);
          setWhere(match.name);
          if ((match.capacity_text || "").trim()) setCapacity(match.capacity_text);
          if ((match.neighborhood || "").trim()) setLocation(match.neighborhood);
          if ((match.address || "").trim()) setAddress(match.address || "");
          setProgramming(normalizeProgramming(match.programming));
          const walk =
            (match.walkthrough_video_url || "").trim() ||
            lookupVenueDir(VENUE_WALKTHROUGH_DIR, match.name);
          if (walk) setWalkthrough(walk);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label="Or search the venue list"
            placeholder={
              venueCatalog.length
                ? `Search ${venueCatalog.length} venues`
                : "Loading venues…"
            }
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          />
        )}
        sx={{
          "& .MuiAutocomplete-popupIndicator": { color: panelMuted },
          "& .MuiAutocomplete-clearIndicator": { color: panelMuted },
        }}
        ListboxProps={{
          sx: {
            bgcolor: "#121820",
            color: panelInk,
            "& .MuiAutocomplete-option": {
              fontFamily: bodyFont,
              fontSize: "0.92rem",
            },
            "& .MuiAutocomplete-option[aria-selected='true']": {
              bgcolor: "rgba(232, 201, 138, 0.16)",
            },
            "& .MuiAutocomplete-option.Mui-focused": {
              bgcolor: "rgba(255,255,255,0.06)",
            },
          },
        }}
        noOptionsText="No matching venues"
      />
      <TextField
        size="small"
        type="date"
        label="Date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={fieldSx}
      />
      <TextField
        size="small"
        label="Location"
        placeholder="Neighborhood or area (e.g. Nolita)"
        value={location}
        onChange={(e) => setLocation(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={fieldSx}
      />
      <TextField
        size="small"
        label="Address"
        placeholder="Street address"
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={fieldSx}
      />
      <TextField
        size="small"
        label="Capacity"
        placeholder="e.g. 80 seated / 120 standing"
        value={capacity}
        onChange={(e) => setCapacity(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={fieldSx}
      />
      <TextField
        select
        size="small"
        label="Comedy or Magic"
        value={programming}
        onChange={(e) => setProgramming(e.target.value === "magic" ? "magic" : "comedy")}
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        sx={fieldSx}
      >
        <option value="comedy">Comedy</option>
        <option value="magic">Magic</option>
      </TextField>
      <TextField
        select
        size="small"
        label="Status"
        value={status}
        onChange={(e) => setStatus(e.target.value === "confirmed" ? "confirmed" : "hold")}
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        sx={fieldSx}
      >
        <option value="confirmed">Confirmed</option>
        <option value="hold">Unconfirmed</option>
      </TextField>
      <FileUploadField
        label="Photo"
        accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,.jpg,.jpeg,.png,.webp,.gif,.pdf"
        hint="JPG, PNG, WebP, GIF, or PDF"
        buttonLabel="Upload image"
        value={photoUrl}
        setValue={setPhotoUrl}
      />
      <FileUploadField
        label="Walkthrough video"
        accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
        hint="MP4/WebM/MOV up to 200 MB, or paste a Google Drive share link. Same video shows on Upcoming after you move the venue."
        buttonLabel="Upload video"
        value={walkthrough}
        setValue={setWalkthrough}
      />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 1.25 }}>
        <TextField
          size="small"
          label="Doors"
          placeholder="e.g. 7:00 PM"
          value={doors}
          onChange={(e) => setDoors(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={fieldSx}
        />
        <TextField
          size="small"
          label="Show start"
          placeholder="e.g. 8:00 PM"
          value={showStart}
          onChange={(e) => setShowStart(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={fieldSx}
        />
        <TextField
          size="small"
          label="Show end"
          placeholder="e.g. 10:00 PM"
          value={showEnd}
          onChange={(e) => setShowEnd(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={fieldSx}
        />
      </Box>
      {error && <Typography sx={{ color: "#ff8a80", fontSize: "0.85rem" }}>{error}</Typography>}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        <Button
          onClick={onSave}
          disabled={saving}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            bgcolor: ctaBlue,
            color: "#0a0e14",
            borderRadius: "999px",
            px: 2,
            "&:hover": { bgcolor: "#b8def5" },
          }}
        >
          {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Save event"}
        </Button>
        <Button onClick={onCancel} disabled={saving} sx={{ textTransform: "none", color: panelInk }}>
          Cancel
        </Button>
        {mode === "edit" && onDelete && (
          <Button
            onClick={onDelete}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 600, color: "#ff8a80", borderRadius: "999px", px: 2 }}
          >
            Delete event
          </Button>
        )}
      </Box>
    </Box>
  );
}

function UpcomingShowPanel({
  ev,
  venues,
  active,
  onOpen,
}: {
  ev: EventRow;
  venues: Venue[];
  active?: boolean;
  onOpen: () => void;
}) {
  const cal = calendarDay(ev.event_date);
  const img = eventImage(ev, venues);
  const brandMark = isEventBrandMark(img);
  const place = shortLocation(ev, venues);
  const title = ev.venue_name || ev.title;
  const confirmed = isConfirmedShow(ev);
  const times = parseEventTimes(ev.partner_notes || "");
  const timingBits = [
    ev.doors_time || times.doors ? `Doors ${ev.doors_time || times.doors}` : "",
    ev.show_start || times.start ? `Show ${ev.show_start || times.start}` : "",
    ev.show_end || times.end ? `Ends ${ev.show_end || times.end}` : "",
  ].filter(Boolean);
  return (
    <Box
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      sx={{
        ...glassCardSx,
        borderRadius: 2.5,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: { xs: 320, md: 360 },
        opacity: confirmed ? 1 : 0.92,
        cursor: "pointer",
        outline: active ? "1px solid rgba(168,212,240,0.55)" : "none",
        transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
        "&:hover": {
          transform: "translateY(-4px)",
          borderColor: "rgba(255,255,255,0.28)",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.1), 0 24px 56px rgba(0,0,0,0.4)",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: { xs: 180, md: 200 },
          bgcolor: "#000000",
        }}
      >
        {brandMark ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: { xs: 3, md: 3.5 },
              py: { xs: 3, md: 3.5 },
            }}
          >
            <Box
              component="img"
              src={img}
              alt=""
              sx={{
                maxHeight: { xs: 110, md: 132 },
                maxWidth: "78%",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                display: "block",
                filter: confirmed ? "none" : "grayscale(1) contrast(0.95) brightness(0.9)",
              }}
            />
          </Box>
        ) : (
          <Box
            component="img"
            src={img}
            alt=""
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: venuePhotoPosition(title),
              display: "block",
              filter: confirmed ? "none" : "grayscale(1) contrast(0.95) brightness(0.9)",
            }}
          />
        )}
        <Box
          sx={{
            position: "absolute",
            top: 14,
            left: 14,
            zIndex: 2,
            bgcolor: "rgba(18,22,30,0.82)",
            border: "1px solid rgba(255,255,255,0.14)",
            borderRadius: 2,
            px: 1.25,
            py: 1,
            textAlign: "center",
            minWidth: 64,
            boxShadow: "0 8px 20px rgba(0,0,0,0.35)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
          }}
        >
          <Typography
            sx={{
              fontFamily: bodyFont,
              fontSize: "0.65rem",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: panelMuted,
              lineHeight: 1,
            }}
          >
            {cal.month}
          </Typography>
          <Typography
            sx={{
              fontFamily: displayFont,
              fontWeight: 800,
              color: panelInk,
              fontSize: "1.65rem",
              lineHeight: 1,
              letterSpacing: "-0.04em",
              my: 0.35,
            }}
          >
            {cal.day}
          </Typography>
          <Typography sx={{ fontFamily: bodyFont, fontSize: "0.7rem", color: panelMuted, lineHeight: 1 }}>
            {cal.weekday}
          </Typography>
          {cal.year && (
            <Typography
              sx={{
                fontFamily: bodyFont,
                fontSize: "0.65rem",
                letterSpacing: "0.08em",
                color: accent,
                fontWeight: 600,
                lineHeight: 1,
                mt: 0.45,
              }}
            >
              {cal.year}
            </Typography>
          )}
        </Box>
        <ProgrammingBadge value={eventProgramming(ev, venues)} corner="top-right" />
        <Box sx={{ position: "absolute", right: 14, bottom: 14, zIndex: 2 }}>
          <Typography
            sx={{
              fontFamily: bodyFont,
              fontSize: "0.65rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: confirmed ? "#0a0e14" : panelInk,
              bgcolor: confirmed ? accent : "rgba(18,22,30,0.82)",
              border: "1px solid rgba(255,255,255,0.14)",
              borderRadius: "999px",
              px: 1.1,
              py: 0.45,
              boxShadow: "0 8px 20px rgba(0,0,0,0.35)",
            }}
          >
            {confirmed ? "Confirmed" : "Unconfirmed"}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 0.6, flex: 1 }}>
        <Typography
          sx={{
            fontFamily: displayFont,
            fontWeight: 700,
            color: panelInk,
            fontSize: "1.2rem",
            letterSpacing: "-0.03em",
            lineHeight: 1.15,
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ fontFamily: bodyFont, color: panelInk, fontSize: "0.92rem" }}>
          {place}
        </Typography>
        {timingBits.length > 0 && (
          <Typography sx={{ fontFamily: bodyFont, color: panelMuted, fontSize: "0.82rem" }}>
            {timingBits.join(" · ")}
          </Typography>
        )}
        <Typography sx={{ fontFamily: bodyFont, color: panelMuted, fontSize: "0.82rem" }}>
          {capacityLabel(resolveEventCapacity(ev, venues))}
        </Typography>
        <Typography sx={{ fontFamily: bodyFont, color: panelMuted, fontSize: "0.8rem", mt: "auto", pt: 1 }}>
          {isFriendsShow(ev) ? "BILT and Friends" : "BILT Neighborhood Comedy"} · {formatDate(ev.event_date)}
        </Typography>
      </Box>
    </Box>
  );
}

function EventDetail({
  ev,
  venues,
  canEdit,
  onEdit,
  onDelete,
  onMoveToUpcoming,
  onMoveToPast,
  onMoveToNewVenues,
  onUpdated,
}: {
  ev: EventRow;
  venues: Venue[];
  canEdit?: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onMoveToUpcoming: () => void;
  onMoveToPast: () => void;
  onMoveToNewVenues: () => void;
  onUpdated?: (ev: EventRow) => void;
}) {
  const times = parseEventTimes(ev.partner_notes || "");
  const place = shortLocation(ev, venues);
  const title = ev.venue_name || ev.title;
  const walkthrough = eventWalkthrough(ev, venues);
  const prog = eventProgramming(ev, venues);
  const today = new Date().toISOString().slice(0, 10);
  const onUpcoming =
    ev.status !== "past" && Boolean(ev.event_date) && ev.event_date.slice(0, 10) >= today;
  const timingBits = [
    ev.doors_time || times.doors ? `Doors ${ev.doors_time || times.doors}` : "",
    ev.show_start || times.start ? `Show ${ev.show_start || times.start}` : "",
    ev.show_end || times.end ? `Ends ${ev.show_end || times.end}` : "",
  ].filter(Boolean);
  const notesWithoutTimesOrNext = (() => {
    const out: string[] = [];
    let inNext = false;
    for (const line of (ev.partner_notes || "").split("\n")) {
      if (/^\s*(Doors|Show start|Show end):/i.test(line)) {
        inNext = false;
        continue;
      }
      if (/^\s*Next steps:\s*$/i.test(line)) {
        inNext = true;
        continue;
      }
      if (inNext) continue;
      out.push(line);
    }
    return out.join("\n").trim();
  })();
  const detailText = [cleanPartnerCopy(ev.description), cleanPartnerCopy(notesWithoutTimesOrNext)]
    .filter(Boolean)
    .join("\n\n");
  const capacity = resolveEventCapacity(ev, venues);
  const [nextSteps, setNextSteps] = useState(eventNextSteps(ev));
  const [nextSaving, setNextSaving] = useState(false);
  const [nextMsg, setNextMsg] = useState("");
  const [comments, setComments] = useState<VenueComment[]>([]);
  const [commentBody, setCommentBody] = useState("");
  const [commentError, setCommentError] = useState("");
  const [commentSaving, setCommentSaving] = useState(false);

  useEffect(() => {
    setNextSteps(eventNextSteps(ev));
    setNextMsg("");
  }, [ev.id, ev.partner_notes, ev.next_steps]);

  useEffect(() => {
    let cancelled = false;
    setComments([]);
    setCommentBody("");
    setCommentError("");
    if (!isUuid(ev.id)) return;
    (async () => {
      const res = await biltFetch<{ comments?: VenueComment[] }>(`events/${ev.id}/`);
      if (cancelled) return;
      if (res.ok && Array.isArray(res.data?.comments)) {
        setComments(res.data.comments);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ev.id]);

  async function saveNextSteps() {
    if (!canEdit || !isUuid(ev.id)) return;
    setNextSaving(true);
    setNextMsg("");
    const authedApi = await ensureBiltApiSession();
    if (!authedApi.ok) {
      setNextSaving(false);
      setNextMsg(authedApi.error || "Please sign in again.");
      return;
    }
    const res = await biltFetch<EventRow>(`events/${ev.id}/`, {
      method: "PATCH",
      body: JSON.stringify({
        where: ev.venue_name || ev.title,
        event_date: (ev.event_date || "").slice(0, 10),
        details: ev.description || "",
        doors_time: times.doors,
        show_start: times.start,
        show_end: times.end,
        venue_id: isUuid(ev.venue_id) ? ev.venue_id : undefined,
        market: ev.market || "NYC",
        programming: prog,
        photo_url: ev.photo_urls?.[0] || "",
        next_steps: nextSteps.trim(),
      }),
    });
    setNextSaving(false);
    if (!res.ok || !res.data) {
      setNextMsg(res.error || "Could not save next steps.");
      return;
    }
    setNextMsg("Saved.");
    onUpdated?.(res.data);
  }

  async function submitEventComment() {
    if (!isUuid(ev.id) || !commentBody.trim()) return;
    const body = commentBody.trim();
    setCommentSaving(true);
    setCommentError("");
    const authedApi = await ensureBiltApiSession();
    if (!authedApi.ok) {
      setCommentSaving(false);
      setCommentError(authedApi.error || "Could not open a BILT API session.");
      return;
    }
    const res = await biltFetch<VenueComment>("comments/", {
      method: "POST",
      body: JSON.stringify({
        subject_type: "event",
        subject_id: ev.id,
        body,
      }),
    });
    if (!res.ok) {
      setCommentSaving(false);
      setCommentError(
        res.error || "Could not save comment to the shared portal. Nothing was saved for other users."
      );
      return;
    }
    setCommentBody("");
    const detailRes = await biltFetch<{ comments?: VenueComment[] }>(`events/${ev.id}/`);
    setCommentSaving(false);
    if (detailRes.ok && Array.isArray(detailRes.data?.comments)) {
      setComments(detailRes.data.comments);
    } else if (res.data) {
      setComments((prev) => [...prev, res.data]);
    }
  }

  return (
    <Box sx={{ display: "grid", gap: 2, color: "#ffffff" }}>
      {(() => {
        const img = eventImage(ev, venues);
        const brandMark = isEventBrandMark(img);
        return (
          <Box
            sx={{
              position: "relative",
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "#000",
              height: 160,
              display: "grid",
              placeItems: "center",
              backgroundImage: `url(${img})`,
              backgroundSize: brandMark ? "55%" : "cover",
              backgroundRepeat: "no-repeat",
              backgroundPosition: brandMark ? "center" : venuePhotoPosition(title),
            }}
          >
            <ProgrammingBadge value={prog} corner="top-right" />
          </Box>
        );
      })()}
      <Typography
        sx={{
          fontFamily: displayFont,
          fontWeight: 700,
          color: "#ffffff",
          fontSize: "1.35rem",
          letterSpacing: "-0.03em",
        }}
      >
        {title}
      </Typography>
      <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.9rem" }}>
        {formatDate(ev.event_date)}
        {place ? ` · ${place}` : ""}
      </Typography>
      <Box>
        <Typography
          sx={{
            fontSize: "0.75rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.65)",
            mb: 0.75,
          }}
        >
          Capacity
        </Typography>
        <Typography sx={{ fontSize: "0.95rem", color: "#ffffff", lineHeight: 1.5 }}>
          {capacity || "Not set yet. Edit this show to add it."}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        <Typography
          sx={{
            fontFamily: bodyFont,
            fontSize: "0.65rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            fontWeight: 700,
            color: prog === "magic" ? "#f5e6ff" : accent,
            bgcolor: prog === "magic" ? "rgba(88, 48, 120, 0.35)" : "rgba(201,168,106,0.12)",
            border: prog === "magic" ? "1px solid rgba(168,120,200,0.45)" : "1px solid rgba(201,168,106,0.35)",
            borderRadius: "999px",
            px: 1.1,
            py: 0.45,
          }}
        >
          {programmingLabel(prog)}
        </Typography>
        <Typography
          sx={{
            fontFamily: bodyFont,
            fontSize: "0.65rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            fontWeight: 700,
            color: accent,
            bgcolor: "rgba(201,168,106,0.12)",
            border: "1px solid rgba(201,168,106,0.35)",
            borderRadius: "999px",
            px: 1.1,
            py: 0.45,
          }}
        >
          {isFriendsShow(ev) ? "BILT and Friends" : "BILT Neighborhood Comedy"}
        </Typography>
      </Box>

      <Box>
        <Typography
          sx={{
            fontSize: "0.75rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.65)",
            mb: 0.75,
          }}
        >
          Timing
        </Typography>
        <Typography sx={{ fontSize: "0.95rem", color: "#ffffff", lineHeight: 1.5 }}>
          {timingBits.length ? timingBits.join(" · ") : "Times not set yet. Edit this show to add them."}
        </Typography>
      </Box>

      <Box>
        <Typography
          sx={{
            fontSize: "0.75rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.65)",
            mb: 0.75,
          }}
        >
          Location
        </Typography>
        <Typography sx={{ fontSize: "0.95rem", color: "#ffffff", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
          {place}
        </Typography>
      </Box>

      <Box>
        <Typography
          sx={{
            fontSize: "0.75rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.65)",
            mb: 0.75,
          }}
        >
          Walkthrough video
        </Typography>
        {walkthrough ? (
          <Box sx={{ display: "grid", gap: 1 }}>
            {isVideoUrl(walkthrough) ? (
              <Box
                component="video"
                src={walkthrough}
                controls
                playsInline
                sx={{ width: "100%", maxHeight: 240, borderRadius: 2, bgcolor: "#000" }}
              />
            ) : null}
            {isGoogleDriveWalkthroughUrl(walkthrough) && googleDrivePreviewUrl(walkthrough) ? (
              <Box
                component="iframe"
                title="Walkthrough preview"
                src={googleDrivePreviewUrl(walkthrough)}
                sx={{
                  width: "100%",
                  height: 240,
                  border: 0,
                  borderRadius: 2,
                  bgcolor: "#000",
                }}
                allow="autoplay"
              />
            ) : null}
            <Button
              href={walkthrough}
              target="_blank"
              rel="noreferrer"
              sx={{ textTransform: "none", color: ctaBlue, px: 0, justifyContent: "start" }}
            >
              {isGoogleDriveWalkthroughUrl(walkthrough)
                ? "Open Google Drive walkthrough"
                : "Open walkthrough"}
            </Button>
          </Box>
        ) : (
          <Typography sx={{ fontSize: "0.95rem", color: "#ffffff", lineHeight: 1.5 }}>
            {canEdit
              ? "Not uploaded yet. Edit this show to add a walkthrough or paste a Google Drive link."
              : "Not uploaded yet."}
          </Typography>
        )}
      </Box>

      {detailText && (
        <Box>
          <Typography
            sx={{
              fontSize: "0.75rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.65)",
              mb: 0.75,
            }}
          >
            Notes
          </Typography>
          <Typography sx={{ fontSize: "0.95rem", color: "#ffffff", whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
            {detailText}
          </Typography>
        </Box>
      )}

      <Box>
        <Typography
          sx={{
            fontSize: "0.75rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.65)",
            mb: 0.75,
          }}
        >
          Next steps
        </Typography>
        {canEdit ? (
          <Box sx={{ display: "grid", gap: 1 }}>
            <TextField
              size="small"
              multiline
              minRows={3}
              placeholder="What still needs to happen for this night?"
              value={nextSteps}
              onChange={(e) => {
                setNextSteps(e.target.value);
                setNextMsg("");
              }}
              InputLabelProps={{ shrink: true }}
              sx={fieldSx}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Button
                onClick={() => void saveNextSteps()}
                disabled={nextSaving}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  bgcolor: ctaBlue,
                  color: "#0a0e14",
                  borderRadius: "999px",
                  px: 2,
                  "&:hover": { bgcolor: "#b8def5" },
                }}
              >
                {nextSaving ? "Saving…" : "Save next steps"}
              </Button>
              {nextMsg && (
                <Typography sx={{ fontSize: "0.85rem", color: nextMsg === "Saved." ? accent : "#ff8a80" }}>
                  {nextMsg}
                </Typography>
              )}
            </Box>
          </Box>
        ) : (
          <Typography sx={{ fontSize: "0.95rem", color: "#ffffff", whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
            {nextSteps.trim() || "None yet."}
          </Typography>
        )}
      </Box>

      {canEdit && (
        <Box sx={{ display: "grid", gap: 1.25, pt: 0.5 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <Button
              onClick={onEdit}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                bgcolor: ctaBlue,
                color: "#0a0e14",
                borderRadius: "999px",
                px: 2,
                "&:hover": { bgcolor: "#b8def5" },
              }}
            >
              Edit event
            </Button>
            <Button
              onClick={onDelete}
              sx={{ textTransform: "none", fontWeight: 600, color: "#ff8a80", borderRadius: "999px", px: 2 }}
            >
              Delete
            </Button>
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.65)",
                mb: 0.75,
              }}
            >
              Move to
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {!onUpcoming && (
                <Button
                  onClick={onMoveToUpcoming}
                  sx={{ textTransform: "none", color: panelInk, border: `1px solid ${mist}`, borderRadius: "999px", px: 1.5 }}
                >
                  Upcoming Shows
                </Button>
              )}
              {onUpcoming && (
                <Button
                  onClick={onMoveToPast}
                  sx={{ textTransform: "none", color: panelInk, border: `1px solid ${mist}`, borderRadius: "999px", px: 1.5 }}
                >
                  Venue & Show Bank
                </Button>
              )}
              <Button
                onClick={onMoveToNewVenues}
                sx={{ textTransform: "none", color: panelInk, border: `1px solid ${mist}`, borderRadius: "999px", px: 1.5 }}
              >
                New Venues
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      <Box>
        <Typography
          sx={{
            fontSize: "0.75rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.65)",
            mb: 1,
          }}
        >
          Feedback
        </Typography>
        {comments.length === 0 && (
          <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.9rem", mb: 1 }}>
            No comments yet.
          </Typography>
        )}
        {comments.map((c) => (
          <Box key={c.id} sx={{ py: 1, borderBottom: `1px solid ${mist}` }}>
            <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.65)" }}>
              {c.author_email} · {new Date(c.created_at).toLocaleString()}
            </Typography>
            <Typography sx={{ fontSize: "0.95rem", color: "#ffffff" }}>{c.body}</Typography>
          </Box>
        ))}
        <TextField
          multiline
          minRows={3}
          fullWidth
          size="small"
          placeholder="Leave a comment"
          value={commentBody}
          onChange={(e) => setCommentBody(e.target.value)}
          sx={{ mt: 1.5, ...fieldSx }}
        />
        {commentError && (
          <Typography sx={{ mt: 1, fontSize: "0.85rem", color: "#ff8a80" }}>{commentError}</Typography>
        )}
        <Button
          onClick={() => void submitEventComment()}
          disabled={commentSaving || !commentBody.trim()}
          sx={{ mt: 1, textTransform: "none", color: "#ffffff" }}
        >
          {commentSaving ? "Posting…" : "Post comment"}
        </Button>
      </Box>
    </Box>
  );
}

function VenueEditorPanel({
  mode,
  name,
  setName,
  address,
  setAddress,
  neighborhood,
  setNeighborhood,
  market,
  setMarket,
  programming,
  setProgramming,
  capacity,
  setCapacity,
  pricing,
  setPricing,
  walkthrough,
  setWalkthrough,
  why,
  setWhy,
  photoUrl,
  setPhotoUrl,
  isNew,
  setIsNew,
  showWalkthrough = true,
  error,
  saving,
  onSave,
  onCancel,
  sx,
}: {
  mode: "add" | "edit";
  name: string;
  setName: (v: string) => void;
  address: string;
  setAddress: (v: string) => void;
  neighborhood: string;
  setNeighborhood: (v: string) => void;
  market: string;
  setMarket: (v: string) => void;
  programming: ProgrammingKind;
  setProgramming: (v: ProgrammingKind) => void;
  capacity: string;
  setCapacity: (v: string) => void;
  pricing: string;
  setPricing: (v: string) => void;
  walkthrough: string;
  setWalkthrough: (v: string) => void;
  why: string;
  setWhy: (v: string) => void;
  photoUrl: string;
  setPhotoUrl: (v: string) => void;
  isNew: boolean;
  setIsNew: (v: boolean) => void;
  showWalkthrough?: boolean;
  error: string;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
  sx?: Record<string, unknown>;
}) {
  return (
    <Box
      sx={{
        ...glassCardSx,
        borderRadius: 2,
        p: { xs: 2, md: 2.25 },
        display: "grid",
        gap: 1.25,
        borderColor: "rgba(168,212,240,0.35)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.1), 0 18px 48px rgba(0,0,0,0.45)",
        ...sx,
      }}
    >
      <Typography sx={{ fontFamily: displayFont, fontWeight: 700, color: panelInk, letterSpacing: "-0.02em" }}>
        {mode === "edit" ? "Edit venue" : "Add venue"}
      </Typography>
      <Typography sx={{ fontFamily: bodyFont, color: panelMuted, fontSize: "0.88rem" }}>
        Name and placement are required. Address, pricing, and a photo help the team spot the room quickly.
      </Typography>
      <TextField
        size="small"
        label="Venue name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={fieldSx}
      />
      <TextField
        size="small"
        label="Address"
        placeholder="Street address"
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={fieldSx}
      />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.25 }}>
        <TextField
          size="small"
          label="Location"
          placeholder="Neighborhood"
          value={neighborhood}
          onChange={(e) => setNeighborhood(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={fieldSx}
        />
        <TextField
          size="small"
          label="Market"
          value={market}
          onChange={(e) => setMarket(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={fieldSx}
        />
      </Box>
      <TextField
        select
        size="small"
        label="Comedy or Magic"
        value={programming}
        onChange={(e) => setProgramming(e.target.value === "magic" ? "magic" : "comedy")}
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        sx={fieldSx}
      >
        <option value="comedy">Comedy</option>
        <option value="magic">Magic</option>
      </TextField>
      <TextField
        size="small"
        label="Capacity"
        placeholder="e.g. 80 seated / 120 standing"
        value={capacity}
        onChange={(e) => setCapacity(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={fieldSx}
      />
      <TextField
        size="small"
        multiline
        minRows={2}
        label="Pricing"
        placeholder="e.g. $15,698 (subtotal from venue tracker)"
        value={pricing}
        onChange={(e) => setPricing(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={fieldSx}
      />
      <TextField
        size="small"
        multiline
        minRows={2}
        label="Short vibe (optional)"
        placeholder="e.g. Dimly lit, good for small-group cocktails"
        value={why}
        onChange={(e) => setWhy(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={fieldSx}
      />
      <FileUploadField
        label="Photo"
        accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,.jpg,.jpeg,.png,.webp,.gif,.pdf"
        hint="JPG, PNG, WebP, GIF, or PDF"
        buttonLabel="Upload image"
        value={photoUrl}
        setValue={setPhotoUrl}
      />
      {showWalkthrough && (
        <FileUploadField
          label="Walkthrough video"
          accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
          hint="MP4/WebM/MOV up to 200 MB, or paste a Google Drive share link"
          buttonLabel="Upload video"
          value={walkthrough}
          setValue={setWalkthrough}
        />
      )}
      <TextField
        select
        size="small"
        label="Show on"
        value={isNew ? "new" : "bank"}
        onChange={(e) => setIsNew(e.target.value === "new")}
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        sx={fieldSx}
      >
        <option value="new">New Venues</option>
        <option value="bank">Venue & Show Bank</option>
      </TextField>
      {error && <Typography sx={{ color: "#ff8a80", fontSize: "0.85rem" }}>{error}</Typography>}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        <Button
          onClick={onSave}
          disabled={saving}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            bgcolor: ctaBlue,
            color: "#0a0e14",
            borderRadius: "999px",
            px: 2,
            "&:hover": { bgcolor: "#b8def5" },
          }}
        >
          {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Save venue"}
        </Button>
        <Button onClick={onCancel} disabled={saving} sx={{ textTransform: "none", color: panelInk }}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
}


function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <Box sx={{ animation: "biltFadeUp 0.45s ease-out both" }}>
      <Typography
        sx={{
          fontFamily: displayFont,
          fontWeight: 700,
          color: ink,
          fontSize: { xs: "1.45rem", md: "1.7rem" },
          letterSpacing: "-0.03em",
          mb: 0.6,
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontFamily: bodyFont, color: muted, mb: 2.25, fontSize: "0.98rem", lineHeight: 1.55 }}>
          {subtitle}
        </Typography>
      )}
      {children}
    </Box>
  );
}

function Empty({ text }: { text: string }) {
  return <Typography sx={{ fontFamily: bodyFont, color: muted, py: 2 }}>{text}</Typography>;
}

function EventRowCard({ ev }: { ev: EventRow }) {
  return (
    <Box
      sx={{
        py: 1.9,
        borderBottom: `1px solid ${mist}`,
        display: "grid",
        gap: 0.4,
        transition: "background-color 160ms ease",
        "&:hover": { bgcolor: "rgba(255,255,255,0.04)" },
      }}
    >
      <Typography sx={{ fontFamily: displayFont, fontWeight: 700, color: ink, letterSpacing: "-0.02em" }}>
        {formatDate(ev.event_date)}
      </Typography>
      <Typography sx={{ fontFamily: bodyFont, color: ink, fontWeight: 500 }}>{ev.venue_name || ev.title}</Typography>
      <Typography sx={{ fontFamily: bodyFont, color: muted, fontSize: "0.85rem" }}>
        {[showTypeLabel(ev.show_type), statusLabel(ev.status)].filter(Boolean).join(" · ")}
      </Typography>
      {ev.partner_notes && (
        <Typography sx={{ fontFamily: bodyFont, fontSize: "0.9rem", mt: 0.5, color: ink }}>{ev.partner_notes}</Typography>
      )}
    </Box>
  );
}

function venuePhotoPosition(name: string) {
  const key = (name || "").toLowerCase();
  // Bias framing so storefronts / rooms aren't cropped to a useless top strip.
  if (key.includes("fini")) return "center 78%";
  if (key.includes("veselka")) return "center 55%";
  if (key.includes("barry")) return "center 62%";
  if (key.includes("porta")) return "center 60%";
  if (key.includes("lokal")) return "center 58%";
  return "center 55%";
}

function VenueCard({
  venue,
  onOpen,
  badge,
}: {
  venue: Venue;
  onOpen: () => void;
  badge?: "new" | "not_activated";
}) {
  const photo = resolveVenueCardPhoto(venue);
  const badgeLabel = badge === "new" ? "New" : badge === "not_activated" ? "Not activated" : null;
  const reactionTag = reactionLabel(venue.reaction || venue.my_reaction);
  return (
    <Box
      component="button"
      onClick={onOpen}
      sx={{
        ...glassCardSx,
        textAlign: "left",
        borderRadius: 1.5,
        overflow: "hidden",
        cursor: "pointer",
        p: 0,
        transition: "transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease",
        "&:hover": {
          transform: "translateY(-4px)",
          borderColor: "rgba(255,255,255,0.28)",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.1), 0 20px 48px rgba(0,0,0,0.4)",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: 200,
          bgcolor: "#000000",
          display: "grid",
          placeItems: "center",
          color: "rgba(255,255,255,0.7)",
          fontSize: "0.8rem",
          overflow: "hidden",
        }}
      >
        {photo ? (
          <Box
            component="img"
            src={photo}
            alt=""
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: venuePhotoPosition(venue.name),
              display: "block",
            }}
          />
        ) : (
          "Photos coming soon"
        )}
        {badgeLabel && (
          <Typography
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              zIndex: 1,
              fontFamily: bodyFont,
              fontSize: "0.65rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              fontWeight: 700,
              color: badge === "new" ? "#0a0e14" : panelInk,
              bgcolor: badge === "new" ? accent : "rgba(18,22,30,0.82)",
              border: "1px solid rgba(255,255,255,0.14)",
              borderRadius: "999px",
              px: 1.1,
              py: 0.45,
              boxShadow: "0 8px 18px rgba(0,0,0,0.35)",
            }}
          >
            {badgeLabel}
          </Typography>
        )}
        <ProgrammingBadge value={venue.programming} corner="top-right" />
        {reactionTag && (
          <Typography
            sx={{
              position: "absolute",
              bottom: 12,
              right: 12,
              zIndex: 1,
              fontFamily: bodyFont,
              fontSize: "0.65rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              fontWeight: 700,
              color: "#0a0e14",
              bgcolor: ctaBlue,
              border: "1px solid rgba(168,212,240,0.45)",
              borderRadius: "999px",
              px: 1.1,
              py: 0.45,
              boxShadow: "0 8px 18px rgba(0,0,0,0.35)",
            }}
          >
            {reactionTag}
          </Typography>
        )}
      </Box>
      <Box sx={{ p: 1.5 }}>
        <Typography sx={{ fontFamily: displayFont, fontWeight: 700, color: panelInk, mb: 0.35, letterSpacing: "-0.02em" }}>{venue.name}</Typography>
        <Typography sx={{ fontFamily: bodyFont, color: panelInk, fontSize: "0.85rem", mb: 0.35 }}>
          {venuePlaceLine(venue)}
        </Typography>
        <Typography sx={{ fontFamily: bodyFont, color: panelMuted, fontSize: "0.82rem", mb: 0.35 }}>
          {capacityLabel(venue.capacity_text)}
        </Typography>
        <Typography sx={{ fontFamily: bodyFont, color: panelMuted, fontSize: "0.82rem", mb: 0.75 }}>
          {(venue.pricing_text || "").trim()
            ? `Pricing ${(venue.pricing_text || "").trim()}`
            : "Pricing not set"}
        </Typography>
        {venue.why_excited && (
          <Typography sx={{ mt: 0.5, fontSize: "0.88rem", color: panelInk, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {cleanPartnerCopy(venue.why_excited)}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function VenueDetail({
  detail,
  commentBody,
  setCommentBody,
  commentError,
  onReact,
  onComment,
  onEdit,
  onMoveToNew,
  onMoveToBank,
  onMoveToUpcoming,
}: {
  detail: Record<string, unknown>;
  commentBody: string;
  setCommentBody: (v: string) => void;
  commentError?: string;
  onReact: (v: string) => void;
  onComment: () => void;
  onEdit: () => void;
  onMoveToNew: () => void;
  onMoveToBank: () => void;
  onMoveToUpcoming: () => void;
}) {
  const venue = detail.venue as Venue;
  const comments = (detail.comments as { id: string; author_email: string; body: string; created_at: string }[]) || [];
  const myReaction = detail.my_reaction as string | null;
  const cardPhoto = resolveVenueCardPhoto(venue);
  const partnerPhoto = (venue.photo_urls || []).find(
    (u) => u && u !== cardPhoto && isImageUrl(u)
  );
  const walkthrough = (venue.walkthrough_video_url || "").trim();
  const prog = normalizeProgramming(venue.programming);
  return (
    <Box sx={{ display: "grid", gap: 2, color: "#ffffff" }}>
      {cardPhoto ? (
        <Box sx={{ position: "relative", borderRadius: 2, overflow: "hidden" }}>
          <Box
            component="img"
            src={cardPhoto}
            alt=""
            sx={{ width: "100%", maxHeight: 200, objectFit: "cover", display: "block" }}
          />
          <ProgrammingBadge value={prog} corner="top-right" />
        </Box>
      ) : null}
      {partnerPhoto && (
        <Box>
          <Typography
            sx={{
              fontSize: "0.75rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.65)",
              mb: 0.75,
            }}
          >
            Uploaded photo
          </Typography>
          <Box
            component="img"
            src={partnerPhoto}
            alt=""
            sx={{ width: "100%", maxHeight: 200, objectFit: "cover", borderRadius: 2 }}
          />
        </Box>
      )}
      <Typography sx={{ fontFamily: displayFont, fontWeight: 700, color: "#ffffff", fontSize: "1.35rem", letterSpacing: "-0.03em" }}>{venue.name}</Typography>
      <Typography
        sx={{
          fontFamily: bodyFont,
          fontSize: "0.65rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          fontWeight: 700,
          color: prog === "magic" ? "#f5e6ff" : accent,
          bgcolor: prog === "magic" ? "rgba(88, 48, 120, 0.35)" : "rgba(201,168,106,0.12)",
          border: prog === "magic" ? "1px solid rgba(168,120,200,0.45)" : "1px solid rgba(201,168,106,0.35)",
          borderRadius: "999px",
          px: 1.1,
          py: 0.45,
          width: "fit-content",
        }}
      >
        {programmingLabel(prog)}
      </Typography>
      <Box>
        <Typography
          sx={{
            fontSize: "0.75rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.65)",
            mb: 0.75,
          }}
        >
          Location
        </Typography>
        <Typography sx={{ fontSize: "0.95rem", color: "#ffffff", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
          {venuePlaceLine(venue)}
        </Typography>
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: "0.75rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.65)",
            mb: 0.75,
          }}
        >
          Capacity
        </Typography>
        <Typography sx={{ fontSize: "0.95rem", color: "#ffffff", lineHeight: 1.5 }}>
          {(venue.capacity_text || "").trim() || "Not set yet. Edit this venue to add it."}
        </Typography>
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: "0.75rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.65)",
            mb: 0.75,
          }}
        >
          Pricing
        </Typography>
        <Typography sx={{ fontSize: "0.95rem", color: "#ffffff", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
          {(venue.pricing_text || "").trim() || "Not set yet. Edit this venue to add subtotal cost."}
        </Typography>
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: "0.75rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.65)",
            mb: 0.75,
          }}
        >
          Walkthrough video
        </Typography>
        {walkthrough ? (
          <Box sx={{ display: "grid", gap: 1 }}>
            {isVideoUrl(walkthrough) ? (
              <Box
                component="video"
                src={walkthrough}
                controls
                playsInline
                sx={{ width: "100%", maxHeight: 240, borderRadius: 2, bgcolor: "#000" }}
              />
            ) : null}
            {isGoogleDriveWalkthroughUrl(walkthrough) && googleDrivePreviewUrl(walkthrough) ? (
              <Box
                component="iframe"
                title="Walkthrough preview"
                src={googleDrivePreviewUrl(walkthrough)}
                sx={{
                  width: "100%",
                  height: 240,
                  border: 0,
                  borderRadius: 2,
                  bgcolor: "#000",
                }}
                allow="autoplay"
              />
            ) : null}
            <Button
              href={walkthrough}
              target="_blank"
              rel="noreferrer"
              sx={{ textTransform: "none", color: ctaBlue, px: 0, justifyContent: "start" }}
            >
              {isGoogleDriveWalkthroughUrl(walkthrough)
                ? "Open Google Drive walkthrough"
                : "Open walkthrough"}
            </Button>
          </Box>
        ) : (
          <Typography sx={{ fontSize: "0.95rem", color: "#ffffff", lineHeight: 1.5 }}>
            Not uploaded yet. Edit this venue to upload a video or paste a Google Drive link.
          </Typography>
        )}
      </Box>
      {cleanPartnerCopy(venue.why_excited) && (
        <Typography sx={{ whiteSpace: "pre-wrap", lineHeight: 1.55, color: "#ffffff" }}>
          {cleanPartnerCopy(venue.why_excited)}
        </Typography>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        <Button
          onClick={onEdit}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            bgcolor: ctaBlue,
            color: "#0a0e14",
            borderRadius: "999px",
            px: 2,
            "&:hover": { bgcolor: "#b8def5" },
          }}
        >
          Edit venue
        </Button>
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: "0.75rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.65)",
            mb: 0.75,
          }}
        >
          Move to
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Button
            onClick={onMoveToUpcoming}
            sx={{ textTransform: "none", color: panelInk, border: `1px solid ${mist}`, borderRadius: "999px", px: 1.5 }}
          >
            Upcoming Shows
          </Button>
          {!venue.is_new_to_portal && (
            <Button
              onClick={onMoveToNew}
              sx={{ textTransform: "none", color: panelInk, border: `1px solid ${mist}`, borderRadius: "999px", px: 1.5 }}
            >
              New Venues
            </Button>
          )}
          {venue.is_new_to_portal && (
            <Button
              onClick={onMoveToBank}
              sx={{ textTransform: "none", color: panelInk, border: `1px solid ${mist}`, borderRadius: "999px", px: 1.5 }}
            >
              Venue & Show Bank
            </Button>
          )}
        </Box>
      </Box>

      <Box>
        <Typography sx={{ fontSize: "0.75rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(255,255,255,0.65)", mb: 1 }}>Your reaction</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {[
            ["interested", "Interested"],
            ["not_a_fit", "Not a fit"],
            ["need_more_info", "Need more info"],
          ].map(([id, label]) => (
            <Button
              key={id}
              size="small"
              variant={myReaction === id ? "contained" : "outlined"}
              onClick={() => onReact(id)}
              aria-pressed={myReaction === id}
              title={myReaction === id ? "Click again to clear" : label}
              sx={{
                textTransform: "none",
                bgcolor: myReaction === id ? accent : "rgba(255,255,255,0.06)",
                color: myReaction === id ? "#0a0e14" : panelInk,
                borderColor: mist,
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                "&:hover": {
                  borderColor: "rgba(255,255,255,0.28)",
                  bgcolor: myReaction === id ? "#d4b87a" : "rgba(255,255,255,0.1)",
                },
              }}
            >
              {label}
            </Button>
          ))}
        </Box>
        {myReaction ? (
          <Typography sx={{ mt: 0.75, fontSize: "0.78rem", color: "rgba(255,255,255,0.55)" }}>
            Tap the selected option again to clear.
          </Typography>
        ) : null}
      </Box>

      <Box>
        <Typography sx={{ fontSize: "0.75rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(255,255,255,0.65)", mb: 1 }}>Feedback</Typography>
        {comments.length === 0 && <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.9rem", mb: 1 }}>No comments yet.</Typography>}
        {comments.map((c) => (
          <Box key={c.id} sx={{ py: 1, borderBottom: `1px solid ${mist}` }}>
            <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.65)" }}>{c.author_email} · {new Date(c.created_at).toLocaleString()}</Typography>
            <Typography sx={{ fontSize: "0.95rem", color: "#ffffff" }}>{c.body}</Typography>
          </Box>
        ))}
        <TextField
          multiline
          minRows={3}
          fullWidth
          size="small"
          placeholder="Leave a comment"
          value={commentBody}
          onChange={(e) => setCommentBody(e.target.value)}
          sx={{ mt: 1.5, ...fieldSx }}
        />
        {commentError ? (
          <Typography sx={{ mt: 1, fontSize: "0.85rem", color: "#ff8a80" }}>{commentError}</Typography>
        ) : null}
        <Button onClick={onComment} sx={{ mt: 1, textTransform: "none", color: "#ffffff" }}>
          Post comment
        </Button>
      </Box>
    </Box>
  );
}
